import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Platform, AlertController } from 'ionic-angular';
import { APP_CONFIG, Env, Region } from '../../environment';
import { Branding } from '../../environment/branding';
import { IRegisterBiometricFingerprintEntity, IRegisterFingerprintEntity } from '../../model/api/auth/register-fingerprint.interface';
import { ISignin, IRegisterFingerprint } from '../../model/models/auth';
import { AuthMapper } from './auth.mapper';
import { KeychainTouchId } from '@ionic-native/keychain-touch-id';
import * as md5 from 'md5';
import { HttpProvider } from '../http/http';
import { DebugErrorProvider, DebugErrorFlow } from '../debug-error/debug-error';
import { CustomerProvider } from '../customer/customer';
import { RecipientProvider } from '../recipient/recipient';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { PaymentProvider } from '../payment/payment';
import { FormControlProvider } from '../formbuilder/formcontrol';
import { OrderProvider } from '../order/order';
import { SendFlowProvider } from '../send-flow/send-flow';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { RegisterProvider } from '../register/register';
import { MarketingProvider } from '../marketing/marketing';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { LoadingProvider } from '../loading/loading';
import { BiometricsType } from '../../model/types/biometrics-type';
import { RouteProvider } from '../route/route';
import { ErrorCodeType } from '../../model/types/error-code-type';
import { TranslateService } from '@ngx-translate/core';
import { ConfigProvider } from '../config/config';
import { ResourceProvider } from '../resource/resource';
import { LoginProvider } from '../../providers/myria/login-provider';
import { PhoneProvider } from '../phone/phone';
import { SegmentProvider } from '../analytics/segment';
import { AnalyticsEventType, ContactModalReasons, SegmentLocationNames, PasscodeChallengeLocation } from '../../model/types/analytics.const';
import { STORAGE_KEYS, USER_FEATURES, LOGIN_ERRORS_NEW_TOKEN, LoginFailureReason, BIOMETRIC_KEY_INVALIDATED, PROMOTION_TYPE,CUSTOMER_SETTINGS, COUNTRY_SETTINGS, COUNTRY_SETTINGS_ENABLE_VALUE, OTP_RESULT, PHONE_VERIFICATION_EVENT_LOCATION, KYC_ATTEMPT_STATUS } from '../myria/app-variables';
import { FeatureFlagTypes, UtilsProvider } from '../myria/utils-provider';
import { resetEkycFlowState } from '../myria/ekyc-flow-state';
import { NotificationProvider } from '../../providers/notification/notification';
import { Globalization } from '@ionic-native/globalization';
import { CustomersProvider } from '../../providers/myria/customer-provider/customers-provider';
import { DeeplinkProvider } from '../../providers/marketing/deeplink';
import { SignInMethod } from '../../model/types/signin-type';
import { WalletProvider } from '../../providers/wallet/wallet';
import { WalletServicesProvider } from '../../providers/wallet-services/wallet-services';
import { User } from '../../model/myria/app/user';
import { IterableProvider } from '../../providers/marketing/iterable';
import { RmtTranslateComponent } from '../../components/rmt-translate/rmt-translate';
import { CallProvider } from '../../providers/call/call';
import { EmailComposer } from '@ionic-native/email-composer';
import { FintocProvider } from '../../providers/fintoc/send-pay-fintoc';
import { LogRocketProvider } from '../../providers/log-rocket/log-rocket';
import { PromoProvider } from '../promo/promo';
import { ProfileNotificationProvider } from '../profile-notification/profile-notification';
import { RmtSnackbarComponent } from '../../components/rmt-snackbar/rmt-snackbar';
import { LoginPasswordType } from '../../model/models/register/user-register';
import { regions } from '../../environment/region/regions';
import { ChatProvider } from '../../providers/zendesk/chat';
import { HomeScreenProvider } from '../../providers/homescreen/homescreen';
import { PermissionProvider } from '../../providers/myria/permission-provider';
import { PermissionType } from '../../pages/permission-tracking/permission-tracking';
import { OtpTimerProvider } from '../../providers/otp-timer/otp-timer';
import { MobileWalletProvider } from '../../providers/mobile-wallet/mobile-wallet-provider';
import { BankDepositSmartRoutingProvider } from '../../providers/bank-deposit-smart-routing/bank-deposit-smart-routing';
import { DeliveryMethodAvailabilityProvider } from '../../providers/delivery-method-availability/delivery-method-availability';
import { VueProvider } from '../vue/vue';
import { VueBridgeContainerHelpers } from '../vue/vue-helpers';
import { NativeProvider } from '../native/native';


declare let Iterable;
declare let AutofillPlugin: any;

export interface IBiometricsKeychainPayload {
  fingerPrintToken?: string;
  version?: number;
}

export type StoredFingerprintTokenFailure = 'cancelled' | 'invalidated' | 'malformed' | 'missing';
export type BiometricLoginFailureReason = StoredFingerprintTokenFailure | 'rejected';

export interface IStoredFingerprintTokenResult {
  token: string | null;
  reason?: StoredFingerprintTokenFailure;
}

const SEGMENT_RESET_SETTING_TIMEOUT_MS = 3000;
// Fingerprint signin RetVals meaning the server no longer accepts the saved token: 10 User Not Found, 130 Invalid Fingerprint.
const REJECTED_FINGERPRINT_RET_VALS = [10, 130];

@Injectable()
export class AuthProvider {
  // These rejections usually arrive with no HTTP status, only Result.RetVal.
  static isFingerprintTokenRejected(ex): boolean {
    const status = ex && (ex.status || (ex.error && ex.error.status) || (ex.errorCurrent && ex.errorCurrent.status));
    const retVal = ex && ex.Result ? Number(ex.Result.RetVal) : null;
    return status === 401 || REJECTED_FINGERPRINT_RET_VALS.indexOf(retVal) !== -1;
  }

  initialized: boolean = false;
  touchId: boolean = false;
  faceId: boolean = false;
  idleTimeout;
  idleTimeoutWarning;
  idleFunction;
  isWhitelistedLogin: boolean = false;
  loginChanged: boolean = false;
  lanuageReady;
  languageResolve;
  customerFieldsPromise;
  initializeUserDetails: any = {};
  lastLogin = null;
  sessionLastLogin = null;
  login: any = null;
  lastLoginTime = null;
  countryChanged: boolean = false;
  invalidCountry: boolean = false;
  languageChanged: boolean = false;
  requirePhoneUpdate: boolean = false;
  loginProfilerStart: Date;
  private pushRegistrationInFlight: boolean = false;
  private lastPushRegistration: number = 0;
  static readonly PUSH_REREGISTER_MIN_INTERVAL_MS = 12 * 60 * 60 * 1000;
  logoutWarning = null;
  isFraudBlocked = false;
  idleEvents = [
    'click',
    'mousedown',
    'mouseup',
    'mousemove',
    'mouseover',
    'mouseout',
    'touchstart',
    'touchend',
    'touchmove',
    'resize',
    'keypress',
    'keydown',
    'keyup',
  ];

  static allowRelogin: boolean = true;
  static blockIdleTimeoutResolve;
  static blockIdleTimeoutPromise;
  static lastLogin = null;
  static lastKycResult = KYC_ATTEMPT_STATUS.IN_PROGRESS;
  static userCountryFieldsVersion = '';

  analyticsMethod: string = "";
  analyticsLocation: string = "";
  rmt5VueBridgeHome: boolean = false;
  lastSigninUsername: string | null = null;
  lastSigninPassword: string | null = null;

  constructor(
    private http: HttpClient,
    private loginProvider: LoginProvider,
    private httpProvider: HttpProvider,
    private platform: Platform,
    private alertCtrl: AlertController,
    private fingerprint: KeychainTouchId,
    private customerProvider: CustomerProvider,
    private storageProvider: StorageProvider,
    private recipientProvider: RecipientProvider,
    private paymentProvider: PaymentProvider,
    private orderProvider: OrderProvider,
    private sendFlowProvider: SendFlowProvider,
    private registerProvider: RegisterProvider,
    private debugError: DebugErrorProvider,
    private loadingProvider: LoadingProvider,
    private marketingProvider: MarketingProvider,
    private formProvider: FormControlProvider,
    private routeProvider: RouteProvider,
    private resourceProvider: ResourceProvider,
    private analytics: SegmentProvider,
    private globalization: Globalization,
    private deeplinkProvider: DeeplinkProvider,
    private utilsProvider: UtilsProvider,
    private notificationProvider: NotificationProvider,
    private translate: TranslateService,
    private customersProvider: CustomersProvider,
    private walletProvider: WalletProvider,
    private email: EmailComposer,
    private iterable: IterableProvider,
    private call: CallProvider,
    private fintocProvider: FintocProvider,
    private logRocket: LogRocketProvider,
    private promoProvider: PromoProvider,
    private homescreenProvider: HomeScreenProvider,
    private vueProvider: VueProvider,
    private chat: ChatProvider,
    private permissionProvider: PermissionProvider,
    private otpTimerProvider: OtpTimerProvider,
    private mobileWalletProvider: MobileWalletProvider,
    private bankDepositSmartRoutingProvider: BankDepositSmartRoutingProvider,
    private deliveryMethodAvailabilityProvider: DeliveryMethodAvailabilityProvider,
    private phoneProvider: PhoneProvider,
    private walletServicesProvider: WalletServicesProvider,
    private nativeProvider: NativeProvider
  ) {
    this.vueProvider.authProvider = this;

    if (APP_CONFIG.env === Env.PROD)
      this.nativeProvider.biometrics.simulate = false; // to be on the safe side

    this.lanuageReady = new Promise((resolve) => {
      this.languageResolve = resolve;
    });
    this.platform.ready().then(() => {
      this.init();

      this.platform.resume.subscribe(async() => {
        HttpProvider.setLastActivityTimeout(); // to be on the safe reset the timeout if it has been started already earlier
        this.resume();
      });

      this.platform.pause.subscribe(async() => {
        LoggerProvider.info(this, 'Application paused');
        // to be on the safe stop the resume timeout to prevent running it in background
        if (HttpProvider.lastActivityTimeout)
          window.clearTimeout(HttpProvider.lastActivityTimeout);
      });
    });

    // simulate resume
    if (!this.platform.is('cordova') && APP_CONFIG.env !== Env.PROD) {
      let simulateExpired = false; // use to simulate the auth token has been expired when getting back to the app
      let visible = true;
      document.addEventListener('visibilitychange', () => {
        LoggerProvider.info(this, 'visibilitychange: ' + document.visibilityState);
        if (document.visibilityState === 'visible') {
          if (AuthProvider.blockIdleTimeoutResolve) {
            LoggerProvider.info(this, 'Resolve block idle timeout');
            AuthProvider.blockIdleTimeoutResolve(true);
          }
          AuthProvider.blockIdleTimeoutPromise = null;
          if (!visible) {
            HttpProvider.setLastActivityTimeout(); // to be on the safe reset the timeout if it has been started already earlier
            if (simulateExpired)
              this.httpProvider.secondsOffset = 100000;
            window.setTimeout(() => {
              this.resume();
            }, 1000); // simulate delay in calling resume
          }
          visible = true;
        } else {
          // to be on the safe stop the resume timeout to prevent running it in background
          if (HttpProvider.lastActivityTimeout)
            window.clearTimeout(HttpProvider.lastActivityTimeout);
          if (!AuthProvider.blockIdleTimeoutPromise) {
            LoggerProvider.info(this, 'Start block idle timeout');
            AuthProvider.blockIdleTimeoutPromise = new Promise((resolve) => { AuthProvider.blockIdleTimeoutResolve = resolve; });
          }
          visible = false;
        }
      });
    }
  }

  async resume() {
    // unfortunately this is very tricky as several different timing behaviour can be noticed on devices
    // differences between the devices:
    // - order of when running resume (some run first some http calls like /log or /refresh)
    // - running setTimeout in the background (some devices keep running it and it triggers then directly when opening the app again)
    // - behaviour of idle (some trigger onIdleStart while in background which means onIdleStart is called first thing when app opens)
    // hence we need to ensure:
    // - block everything until resume could eventually get into checkRelogin to check if a relogin is necessary
    // - no http calls like /log before relogin situation has been clarified otherwise ensureRemittanceAuth will result either in a refresh or a route to start
    LoggerProvider.info(this, 'Application resumed');
    HttpProvider.checkLastActivity();

    // resume has been called hence notify HttpProvider (500ms delay to let the relogin logic run through)
    window.setTimeout(() => {
      if (HttpProvider.inactivityResolve)
        HttpProvider.inactivityResolve(true);
      if (HttpProvider.inactivityPromise) {
        HttpProvider.inactivityPromise = null;
        LoggerProvider.info(this, 'Cleared inactivity promise in resume');
      }
    }, 500);

    if (HttpProvider.relogging) {
      LoggerProvider.info(this, 'Application resumed but is already doing a relogin');
      return;
    }

    if (this.httpProvider.isAuthTokenExpired()) {
      LoggerProvider.info(this, 'Application resumed with expired token');

      const result = await this.checkRelogin();
      if (result) {
        this.refreshPushRegistrationOnResume();
        return true;
      } else {
        LoggerProvider.info(this, 'Relogin failed');
        await this.logoutUser();
      }
    }
    LoggerProvider.info(this, 'Application resumed will try to refresh');
    this.httpProvider.checkRefresh();

    // OM-18569 | Force Token Refresh on App Open | Aggressive battery management on many Android devices kills the FCM background
    this.refreshPushRegistrationOnResume();
  }

  private async refreshPushRegistrationOnResume() {
    if (!this.platform.is('cordova'))
      return;

    if (!this.httpProvider.getAuthCustomerId() && !this.httpProvider.getAuthUserId())
      return;

    if (this.pushRegistrationInFlight) // Prevent multiple push registration calls in parallel
      return;

    const now = new Date().getTime();
    if (now - this.lastPushRegistration < AuthProvider.PUSH_REREGISTER_MIN_INTERVAL_MS)
      return;

    this.pushRegistrationInFlight = true;
    try {
      if (!await this.permissionProvider.checkNotificationPermission())
        return;
      await this.registerIterable();
      this.lastPushRegistration = now;
    } catch (error) {
      LoggerProvider.info(this, 'Push re-registration on resume failed: ' + error);
    } finally {
      this.pushRegistrationInFlight = false;
    }
  }

  startLoginProfiler() {
    this.loginProfilerStart = new Date();
  }

  endLoginProfiler() {
    if (!this.loginProfilerStart)
      return;

    const duration =  new Date().getTime() - this.loginProfilerStart.getTime();
    if (duration >= 2000)
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Login profiler: slow (' + duration + ' ms)');
    else if (duration >= 1000)
      LoggerProvider.info(this, 'Login profiler: normal (' + duration + ' ms)');
    else
      LoggerProvider.debug(this, 'Login profiler: quick (' + duration + ' ms)');
    this.loginProfilerStart = null;
  }

  async initLanguage() {
    let lang = window.navigator.language;
    let currentSettingLang = await this.storageProvider.get(StorageType.LastDeviceLanguage);
    let lastLogin = await this.storageProvider.get(StorageType.LastLogin);

    if(lang && currentSettingLang != lang && !lastLogin){
      await this.storageProvider.set(StorageType.LastDeviceLanguage, lang);
      let langSplit = lang.split('-');
      if(langSplit.length == 2) lang = langSplit[0] + '-' + langSplit[1].toUpperCase();
      this.selectLanguage(lang, false, false);
    } else {
      try {
        const language = await this.customerProvider.getStoredLanguage();
        if (language) {
          const langs = this.utilsProvider.availableLanguageCultures();
          for (let i=0; i < langs.length; i++) {
            const lang = langs[i];
            if (lang.indexOf(language) === 0) {
              this.customerProvider.setLanguage(language, false, false);
              return;
            }
          }
        }
        this.customerProvider.setLanguage(APP_CONFIG.region.defaultLanguageCulture, false, false);
      }
      catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to initialize language in customer provider');
      }
    }
  }

  async selectLanguage(lang, reset = true, event = true) {
    if (event) {
      this.analytics.trackEvent({
        eventName: AnalyticsEventType.LanguageSelected, analyticsData: {
          location: 'landing screen',
          language: lang,
        }
      });
    }

    await this.customerProvider.setLanguage(lang, true, reset);
  }

  async init() {
    const country = await this.getUserCountry();
    await this.setUserCountry(country, false, false, false, true, false, false); // field reset will be done later to not do unnecessary calls when we do a biometrics login
    await this.initLanguage();
    this.languageResolve();
    FormControlProvider.readyResolve();

    if (!this.nativeProvider.biometrics.initialized) this.biometricsAvailable();
  }

  async initializeUser(resetOrder = false, setMarketing: boolean = false, setTransactionNotification: boolean = false, skipMarketing: boolean = false, hasAlreadyUser: boolean = false, registrationMarketingOptIn: boolean = null) {
    try {
      LoggerProvider.info(this, 'Initialize user');

      await this.logout(true, false, false); // don't reset login

      this.storageProvider.remove(StorageType.AgentReferralToken); // successful login means that there should not be an open agent referral anymore hence remove it
      this.storageProvider.remove(StorageType.AgentReferralData);

      this.initialized = true;
      this.lastLoginTime = new Date().getTime();
      this.initTimer();

      if (this.login && this.login.User) {
        LoggerProvider.info(this, 'Initialize user from login');
        this.loginProvider.setUser(this.login.User);
      }

      LoggerProvider.generateSessionId();

      let initialCountry = await this.getUserCountry();
      //await this.saveUserInfo(); eventually will be needed again depending on the final backend solution for user version - has been moved below to avoid calling it twice for users with user version

      this.isWhitelistedLogin = false;
      const authDetails = this.httpProvider.getAuthDetails();
      if (authDetails) {
        const userVersions = {
          //'rmt3-610_262@mailinator.com': '3.40.0',
          //'1598955': '3.40.0',
          'huzefabagdadi@yahoo.co.in': '3.40.0',
          'shresthabk@gmail.com': '3.40.0',
          'rehman.fahad@hotmail.com': '3.40.0',
          'cklim@riafinancial.com': '3.40.0',
          'norazanita@yahoo.com': '3.40.0',
          'kanwalameer@gmail.com': '3.40.0',
          '53698211': '3.40.0',
          '1976607828': '3.40.0',
          '5587190': '3.40.0',
          '44023302': '3.40.0',
          '235952811': '3.40.0',
          '515066590': '3.40.0',
          'khalifahjawa@gmail.com': '3.40.0',
          'mraees60@gmail.com': '3.40.0',
          'rizwan157@gmail.com': '3.40.0',
          'basant78@gmail.com': '3.40.0',
          '223568511': '3.40.0',
          '967886834': '3.40.0',
          '176173511': '3.40.0',
          '9105893': '3.40.0',
          '87808111': '3.40.0',
          '193066011': '3.40.0',
          '178593211': '3.40.0',
          '203444811': '3.40.0',
          '102334211': '3.40.0',
          '8342193': '3.40.0',
          '884793': '3.40.0',
          'Kanwalameer@gmail.com': '3.40.0',
          '122411': '3.40.0',
          'bthapa@riafinancial.com': '3.40.0',
          '7735493': '3.40.0',
          'ramesh_chaulagain@yahoo.com': '3.40.0',
          '176126311': '3.40.0',
          'dhakalsp1@gmail.com': '3.40.0',
          '6508593': '3.40.0',
          'srahaman@riafinancial.com': '3.40.0',
          '8465793': '3.40.0',
          'ekristensen@riafinancial.com': '3.40.0',
          '221569411': '3.40.0',
          'nurhafizi.ghazali@gmail.com': '3.40.0',
          '60164464753': '3.40.0',
          '932293': '3.40.0',
          'javedansari01@yahoo.com': '3.40.0',
          '187182011': '3.40.0',
          '60146983176': '3.40.0',
          '194053911': '3.40.0',
          'bpokhrel@riafinancial.com': '3.40.0',
          'anandini.gunasekaran@gmail.com': '3.40.0',
          '277322011': '3.40.0',
          'armiendin80@gmail.com': '3.40.0',
          '178653311': '3.40.0',
          'biku_2k@hotmail.com': '3.40.0',
          '175864911': '3.40.0',
          'eddywongkl@gmail.com': '3.40.0',
          '194055311': '3.40.0',
          'kala.vicky33@gmail.com': '3.40.0',
          '248565111': '3.40.0',
          'satishverma77@hotmail.com': '3.40.0',
          '7898693': '3.40.0',
          // phone numbers TODO
          '60172188120': '3.40.0',
          '60149021031': '3.40.0',
          '60122128731': '3.40.0',
          '601125566486': '3.40.0',
          '60123856750': '3.40.0',
          '60123863400': '3.40.0',
          '60124170335': '3.40.0',
          '60122941447': '3.40.0',
          '60125007859': '3.40.0',
          '60173444579': '3.40.0',
          '60192050150': '3.40.0',
          '60124644899': '3.40.0',
          '60122242715': '3.40.0',
          '60102884036': '3.40.0',
          '60193697394': '3.40.0',
          '60122997109': '3.40.0',
          '60173080378': '3.40.0',
          // VPA test users
          'mfojedawallet@gmail.es': '3.40.0',
          'belenfernandez.st@gmail.com': '3.40.0',
          // 'moemen.a1@gmail.com': '3.40.0',
        };
        let versionChanged = '';
        if (authDetails.email && userVersions[authDetails.email.toLowerCase()]) {
          HttpProvider.minVersion = userVersions[authDetails.email.toLowerCase()];
          versionChanged = 'username';
        }
        else if (authDetails.userId && userVersions[authDetails.userId]) {
          HttpProvider.minVersion = userVersions[authDetails.userId];
          versionChanged = 'user ID';
        }
        if (versionChanged) {
          LoggerProvider.info(this, 'Use user version by ' + versionChanged + ': ' + HttpProvider.minVersion);
          this.isWhitelistedLogin = true;
          initialCountry = null; // to force reloading the country features
          //await this.saveUserInfo(); eventually will be needed again depending on the final backend solution for user version
        }
      }

      this.languageChanged = false;
      const language = this.customerProvider.getCurrentLanguage();
      // originally before userVersions it will depend on the final backend solution for user version - now we do it afterwards so we don't have to call saveUserInfo twice for users in userVersions
      await this.saveUserInfo(setMarketing, skipMarketing, registrationMarketingOptIn);
      if (language !== this.customerProvider.getCurrentLanguage()) {
        LoggerProvider.info(this, 'Language changed');
        this.languageChanged = true;
      }

      let countryPromise = null;
      const country = await this.getUserCountry();
      if (country !== initialCountry || this.invalidCountry) {
        LoggerProvider.info(this, 'Country changed');
        countryPromise = this.utilsProvider.refreshCountryFeatures();
      }

      LoggerProvider.info(this, 'Init refresh timeout');
      this.httpProvider.startRefreshTimer();
      this.initializeUserDetails = {
        resetOrder,
        setMarketing,
        setTransactionNotification
      };

      if (this.loginProvider.isLiteUser()) {
        this.customerFieldsPromise = this.customerProvider.saveLiteCustomerFields();
        this.customerProvider.callNotificationSubscriptions(setMarketing);
      }
      await countryPromise;

      await this.walletProvider.checkCountrySettings();

      if (!this.loginProvider.isLiteUser()) {

        if (APP_CONFIG.featureFlags.signinOld) {
          let user = null;
          if (hasAlreadyUser)
            user = this.loginProvider.setUser(HttpProvider.user);
          else
            user = await this.loginProvider.getUserAsync();
          if (user && user.Customers && user.Customers.length > 0) {
            this.login = {};
            this.login.FirstName = user.Customers[0].FirstName;
          }
          if(user) {
            this.login.MustAcceptedNewLicense = user.MustAcceptedNewLicense;
            this.login.RequireTermsAndCondition = user.RequireTermsAndCondition;
          }
        }
      }

      if (!this.login || (this.login && !this.login.FirstName)) {
        let user = null;
        if (hasAlreadyUser)
          user = this.loginProvider.setUser(HttpProvider.user);
        else
          user = await this.loginProvider.getUserAsync();

        if (user && user.Customers && user.Customers.length > 0) {
          this.login = {};
          this.login.FirstName = user.Customers[0].FirstName;
        }
      }
      return true;
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to initialize user', ex);
    }
    return false;
  }

  async getCurrentSigninResources(resetOrder, setMarketing, setTransactionNotification) {

    if(resetOrder) this.orderProvider.initializeOrders();
    this.customerProvider.initialize(true, 0, {}, setMarketing, setTransactionNotification); // Moving this out from the below condition as identify call and queued events are not triggering
    if(!this.walletProvider.hasWallet()){
      const customer = this.loginProvider.getUserCustomer();
      if (customer &&
          typeof(customer.WillReceivePushMarketing) !== 'undefined' &&
          typeof(customer.WillReceivePushNotifications) !== 'undefined') {
        LoggerProvider.info(this, 'Call identify with user data');
        this.loginProvider.identifyUser();
      }
      this.recipientProvider.initializeRecipients(true);
    }
    this.registerProvider.setCurrentRegistration({});
    this.registerProvider.setCurrentRegistrationInLoginToSend({});
    this.registerProvider.setCurrentRegistrationInLoginToSendChanged({});
    this.marketingProvider.initialize(true);

  }

  async navigateLogin(signin: any, analyticsLocation = SegmentLocationNames.Login, checkBiometrics = true) {
    return new Promise<void>(async (resolve) => {
      let available = false;
      if (checkBiometrics && signin && this.loginChanged) {
        LoggerProvider.trace(this, 'Check if biometrics are available');
        available = await this.biometricsAvailable();
        LoggerProvider.trace(this, 'Biometrics available: ' + available);
      }

      if (this.requirePhoneUpdate) {
        this.requirePhoneUpdate = false;
        const skipUser = await this.utilsProvider.hasFeatureFlag(FeatureFlagTypes.SkipUserForRequirePhoneUpdate);
        let phone = null;
        if (!skipUser && HttpProvider.user && HttpProvider.user.LastVerifiedPhone) {
          LoggerProvider.info(this, 'Use require phone update flow with user data');
          phone = {
            "countryCode": HttpProvider.user.LastVerifiedPhone.CountryCode,
            "phoneNumber": this.phoneProvider.stripDuplicateCountryCode(HttpProvider.user.LastVerifiedPhone.CountryCode, HttpProvider.user.LastVerifiedPhone.Number)
          };
        } else {
          LoggerProvider.info(this, 'Use require phone update flow with customer data');
          const customer = await this.customerProvider.getCustomer();
          const customerCountryCode = (customer && customer.mobilePhone && customer.mobilePhone.countryCode) ? customer.mobilePhone.countryCode.toString() : '';
          const customerPhoneNumber = (customer && customer.mobilePhone) ? customer.mobilePhone.number : '';
          phone = {
            "countryCode": customerCountryCode,
            "phoneNumber": this.phoneProvider.stripDuplicateCountryCode(customerCountryCode, customerPhoneNumber)
          };
        }
        this.routeProvider.push(this.routeProvider.pages.MobileVerifyPage, { 
          signin,
          phone,
          isPhoneUpdate: true,
          onBackCallback: () => {
            this.navigateLogin(signin, analyticsLocation, checkBiometrics);
          }
        });
      } else if (available) {
        this.routeProvider.replace(this.routeProvider.pages.RegistrationBiometricsPage, {
          password: signin.password,
          analyticsLocation: analyticsLocation,
        });
      } else
        await this.continueNavigateLogin();
      resolve();
    });
  }

  async checkPermissions(modal: boolean = true): Promise<boolean> {
    this.identifyToIterable();

    const requestNotificationPermission = async (): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        this.routeProvider.pushOrModal(
          modal,
          this.routeProvider.pages.PermissionTrackingPage,
          { permissionType: PermissionType.NOTIFICATION },
          async () => {
            this.permissionProvider.checkNotificationPermission().then((hasPermission) => {
              resolve(true);
              if (hasPermission) {
                this.registerIterable()
              }
            });
          }
        );
      });
    };

    const requestLocationPermission = async (): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        this.routeProvider.pushOrModal(
          modal,
          this.routeProvider.pages.PermissionTrackingPage,
          { permissionType: PermissionType.LOCATION },
          () => {resolve(true);}
        );
      });
    };

    // the elevated RMT5 relogin iframe covers any page pushed now, so the relogin would wait for the logout timer
    const warnIfCoveredByVueRelogin = (permissionType: PermissionType) => {
      if (HttpProvider.vueReloginInProgress)
        LoggerProvider.warning(this, LoggerEvent.Vue, 'Permission screen requested during an RMT5 relogin: ' + permissionType);
    };

    const accessedNotificationScreen = await this.permissionProvider.checkNotificationPermissionScreenAccessed();

    if (!accessedNotificationScreen) {
      warnIfCoveredByVueRelogin(PermissionType.NOTIFICATION);
      await requestNotificationPermission();
    } else {
      const hasPermission = await this.permissionProvider.checkNotificationPermission();
      if (hasPermission) {
        this.registerIterable();
      }
    }

    const accessedLocationScreen = await this.permissionProvider.checkLocationPermissionScreenAccessed();

    if (!accessedLocationScreen) {
      warnIfCoveredByVueRelogin(PermissionType.LOCATION);
      await requestLocationPermission();
    }

    return true; // If we reach here, both permissions are (or have been) granted.
  }

  async requestNotificationPermissionBridge(): Promise<boolean> {
    this.identifyToIterable();
    const granted = await this.permissionProvider.requestNotificationPermission();
    if (granted)
      await this.registerIterable();
    return granted;
  }

  // 60s: the OS prompt can outlast the default 5s.
  async requestLocationPermissionBridge(): Promise<boolean> {
    return this.permissionProvider.requestLocationPermission(60000);
  }

  // 'U' + null === 'Unull' is truthy, which would pool every id-less device on one profile.
  private resolveIterableId(): string | null {
    const customerId = this.httpProvider.getAuthCustomerId();
    if (customerId)
      return customerId;
    const userId = this.httpProvider.getAuthUserId();
    return userId ? 'U' + userId : null;
  }

  // Switching SN to a real email mid-lifecycle orphans the device on a second record.
  private getIterableIdentityEmail(): string | null {
    const id = this.resolveIterableId();
    if (!id)
      return null;
    if (APP_CONFIG.region.region === Region.SN)
      return id + '@placeholder.email';
    return this.httpProvider.getAuthEmail() || (id + '@placeholder.email');
  }

  // Must run before any OS push prompt or the device token uploads as an orphan.
  private identifyToIterable(): void {
    if (!this.platform.is('cordova'))
      return;
    const email = this.getIterableIdentityEmail();
    if (email)
      Iterable.setEmail(email);
  }

  // Bridge onboarding skips checkPermissions(); rebind without ever prompting.
  async rebindPushRegistrationForSession(): Promise<void> {
    if (!this.platform.is('cordova'))
      return;
    this.identifyToIterable();
    const hasPermission = await this.permissionProvider.checkNotificationPermission();
    if (hasPermission)
      await this.registerIterable();
  }

  async afterLogin(usualLoginFlow) {
    await this.routeProvider.transitionPromise;
    if (this.countryChanged)
      this.resetUserCountryFields(false, false);
    this.countryChanged = false;
    this.vueProvider.prepareLogin(this.lastSigninUsername);
    this.lastSigninPassword = null;

    if (!this.loginProvider.isLiteUser() && this.initializeUserDetails)
      this.getCurrentSigninResources(this.initializeUserDetails.resetOrder, this.initializeUserDetails.setMarketing, this.initializeUserDetails.setTransactionNotification);
    if (!this.loginProvider.isLiteUser() && usualLoginFlow) {
      const order = await this.orderProvider.getPendingOrder();
      if (order && !order.displayedReminder) {
        order.displayedReminder = true;
        this.orderProvider.updatePendingOrder(order);
        this.routeProvider.openActionSheetNew(this.routeProvider.pages.SendContinuePage, { order });
      }
    }
  }

  async continueNavigateLogin(): Promise<any> {
    return new Promise<void>(async (resolve) => {
      await this.mustAcceptLTLicense();
      // not awaited: analytics only, its api/Wallet call must not hold up navigation.
      // getWalletStatusInString sets firstRefresh only after api/Wallet answers, so set it now for WalletHomePage.
      this.walletProvider.setFirstRefresh(true);
      this.logLoginSuccessfulEvent().catch((ex) => LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to log login successful event'));
      if (this.loginProvider.isLiteUser()) {
        await this.customerFieldsPromise;
        const handled = await this.deeplinkProvider.handleOnLogin();
        if (!handled) {
          await this.utilsProvider.ensureFeatureFlags();
          const rmt5VueBridgeHome = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeHome);
          let homeRedirected = false;
          // US wallet pilot: onboarding outranks RMT5Home for a customer without a wallet.
          if (this.walletProvider.isEnabled() && this.walletProvider.isWalletSupportEnabled() && !this.walletProvider.hasWallet()) {
            LoggerProvider.info(this, 'Routing bridge to WalletOnboarding after biometrics');
            await this.vueProvider.navigateWithFreshSession('WalletOnboarding');
            // Must verify the bridge actually landed, exactly as the RMT5Home branch does.
            // Assuming success here strands the user on the biometrics page with no fallback
            // whenever the shipped bundle has no WalletOnboarding route or the bridge is off.
            homeRedirected = await this.vueProvider.waitForTransition();
            if (!homeRedirected) {
              LoggerProvider.info(this, 'WalletOnboarding bridge redirect failed, falling back');
            }
          } else if (rmt5VueBridgeHome) {
            this.vueProvider.navigate('RMT5Home', '', {}, null, true);
            homeRedirected = await this.vueProvider.waitForTransition();
            if (homeRedirected) {
              LoggerProvider.info(this, 'Lite user redirect to Vue home page successful');
            } else {
              LoggerProvider.info(this, 'Lite user redirect to Vue home page failed, falling back');
            }
          }
          if (!homeRedirected) {
            this.routeProvider.setRoot(this.routeProvider.pages.TabsHomePage);
          }
        }

        this.homescreenProvider.setHomeScreenLocation(SegmentLocationNames.Activity)
      } else
        await this.loginFirstRedirection();
      resolve();
    });
  }

  async isProfileCompleted(onComplete, customerFieldsPromise: any = null) {
    if (!customerFieldsPromise)
      customerFieldsPromise = this.customerProvider.getCustomerFields();
    const customerFields = await customerFieldsPromise;
    let hasFields = false;
    if (customerFields && customerFields.Value) {
      for (let c = 0; c < customerFields.Value.length; c++) {
        const category = customerFields.Value[c];
        if (category.Fields) {
          for (let f = 0; f < category.Fields.length; f++) {
            const field = category.Fields[f];
            if (!field.Readonly) {
              hasFields = true;
              break;
            }
          }
          if (hasFields)
            break;
        }
      }
    }

    if (customerFields.Result.RetVal === 1) {
      LoggerProvider.info(this, 'No missing customer profile fields');
      return true;
    } else if (!hasFields) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Should show missing fields but has no fields ? RetVal: ' + customerFields.Result.RetVal);
      return true;
    } else {
      let page = 0;
      let lastStep = !(customerFields.Result.RetVal === 20);
      if (customerFields.Result.RetVal === 30 || customerFields.Result.RetVal === 20) page = 0;
      if (customerFields.Result.RetVal === 40) page = 1;
      this.customerProvider.setCustomerFields(customerFields);
      let params = {
        showClose: true,
        img: 'assets/icons/Figure.svg',
        title: this.translate.instant('page.settingsPersonalPage.missingInfoHeader'),
        msg: this.translate.instant('page.settingsPersonalPage.missingInfoBody'),
        primaryButtonText: this.translate.instant('global.myRia.jsonContent.LoginRequiredFieldsBtn'),
        primaryButtonAction: () => {
          this.requiredFields(customerFields, page, lastStep, onComplete);
        },
      };
      this.loadingProvider.hide();
      this.routeProvider.push(this.routeProvider.pages.InfoPage, params);
    }
    return false;
  }

  async loginFirstRedirection() {
    let usualLoginFlow = true;
    // load flags before the wallet routing decision so the sync read can't race the session.
    await this.utilsProvider.ensureFeatureFlags();
    const hasActiveWallet = this.walletProvider.hasWallet();
    this.rmt5VueBridgeHome = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeHome);
    const walletSupportEnabled = this.walletProvider.isWalletSupportEnabled();
    const isNonWalletPilotState = await this.walletProvider.shouldShowWalletWaitlist();
    // SN/MY wallet holders land on the native wallet home: load its data during the route transition.
    const isNativeWalletRegion = APP_CONFIG.region.region === Region.SN || APP_CONFIG.region.region === Region.MY;
    if (isNativeWalletRegion && hasActiveWallet && !walletSupportEnabled && !isNonWalletPilotState)
      this.walletProvider.prefetchWalletHome();
    // the native wallet tab exists only for the MY/SN wallet; a US pilot holder falls back to Activity.
    const rootPage = isNonWalletPilotState ? this.routeProvider.pages.WalletWaitlistPage : ((hasActiveWallet && !walletSupportEnabled) ? this.routeProvider.pages.TabsHomeWalletPage : this.routeProvider.pages.TabsHomeActivityPage);
    try {
      const handled = await this.deeplinkProvider.handleOnLogin();
      if (handled)
        usualLoginFlow = false;
      else {
        if ((this.rmt5VueBridgeHome || walletSupportEnabled) && !isNonWalletPilotState) {
          // wallet holders land on WalletHome; everyone else on RMT5Home.
          const routeToVuePage = (walletSupportEnabled && hasActiveWallet) ? 'WalletHome' : 'RMT5Home';
          this.vueProvider.navigate(routeToVuePage, '', {}, null, true);
          const success = await this.vueProvider.waitForTransition();
          if (success) {
            LoggerProvider.info(this, 'Redirect to Vue ' + routeToVuePage + ' page successful');
            // The Vue iframe now covers the app, but the native stack underneath is still
            // the sign-in flow. Leave a logged-in root there so that anything which hides
            // the iframe later (Vue back on its start page, hardware back) reveals the home
            // tabs and not the login screen.
            await this.vueProvider.waitForBridgeCover();
            this.routeProvider.setRoot(this.routeProvider.pages.TabsHomePage, {}, false);
            return;
          }
          LoggerProvider.info(this, 'Redirect to Vue home page failed hence fallback to normal login flow');
        }
        this.routeProvider.setRoot(rootPage);
        this.loadingProvider.hide();
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get referral promo');
      this.routeProvider.setRoot(rootPage); // fallback in case previous setRoot failed
      this.deeplinkProvider.setDeepLinkShowReferralParam(null);
    }

    this.afterLogin(usualLoginFlow);
  }

  hasReferralPromo(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const resPromotion = this.loginProvider.isLiteUser() ? await this.customersProvider.getLRUserPromotions() : await this.customersProvider.getCustomerPromotions();
        let winbackPromo = await this.checkPromos(resPromotion.Promotions, PROMOTION_TYPE.WINBACK);
        let customerReferralPromo = await this.checkPromos(resPromotion.Promotions, PROMOTION_TYPE.CUSTOMERREFERRAL);
        if (winbackPromo || customerReferralPromo)
          resolve(customerReferralPromo ? customerReferralPromo : winbackPromo);
        else
          resolve (false);
      }catch(ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get referral promo');
        reject();
      }
    });
  }

  checkPromos(promotions, promotionType){
    return new Promise(async (resolve, reject) => {
      let promoRes = null;
      promotions.forEach(promo => {
        if(promo.PromoId === promotionType && promo.IsReferral === true){
          promoRes = promo;
        }
      });
      resolve(promoRes);
    });
  }

  async requiredFields(customerFields: any, page: number, lastStep: boolean, onComplete) {
    const customerValues = {};
    if (customerFields && customerFields.Value) {
      this.analytics.trackEvent({
        eventName: AnalyticsEventType.BiometricsVerificationSuccesful
      });

      for (let c = 0; c < customerFields.Value.length; c++) {
        const category = customerFields.Value[c];
        for (let f = 0; f < category.Fields.length; f++) {
          const field = category.Fields[f];
          customerValues[field.FieldId] = field.Value;
        }
      }
      this.routeProvider.replace(this.routeProvider.pages.LoginStepDynamicPage, { step: page, values: customerValues, lastStep: lastStep, customerFields: customerFields.Value, onComplete });
    }
  }

  async logout(resetOrder = true, resetSession: boolean = false, resetLogin: boolean = true) {
    this.initialized = false;
    this.customerFieldsPromise = null;
    AuthProvider.lastKycResult = KYC_ATTEMPT_STATUS.IN_PROGRESS;
    resetEkycFlowState(); // clear US wallet eKYC flow state so it can't leak across sessions/users

    if (resetLogin)
      this.login = null;
    this.lastLoginTime = null;
    if (resetOrder) this.orderProvider.logout();
    this.customerProvider.logout();
    this.customersProvider.logout();
    this.recipientProvider.logout();
    this.sendFlowProvider.logout();
    this.paymentProvider.logout();
    this.formProvider.logout();
    this.walletProvider.logout();
    this.walletServicesProvider.logout();
    this.marketingProvider.initialize(true);
    if (resetLogin)
      this.loginProvider.logout();
    this.promoProvider.logout();
    this.chat.logout();
    this.mobileWalletProvider.logout();
    this.bankDepositSmartRoutingProvider.logout();
    this.deliveryMethodAvailabilityProvider.logout();
    ProfileNotificationProvider.logout();
    RegisterProvider.restMarketingOptCheck();

    if (resetLogin) {
      this.deeplinkProvider.clearDeeplinkReferral();
      HttpProvider.featureFlagsUser = {};
      this.httpProvider.updateFeatureFlags('logout');
      this.storageProvider.remove(StorageType.AgentReferralToken);
      this.storageProvider.remove(StorageType.AgentReferralData);
    }

    this.unsubscribeTimer();
  }

  unsubscribeTimer(log = true) {
    if (log)
    LoggerProvider.info(this, 'Unsubscribe idle timer');
    if (this.idleTimeoutWarning) {
      window.clearTimeout(this.idleTimeoutWarning);
      this.idleTimeoutWarning = null;
    }
    if (this.idleTimeout) {
      window.clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }

    if (this.idleFunction) {
      for (const ev of this.idleEvents) {
        document.body.removeEventListener(ev, this.idleFunction);
    }
  }
    this.idleFunction = null;
  }

  async logoutUser(page = this.routeProvider.pages.PreLoginPage, showLoader: boolean = true, isAutoLogBackInBiometric: boolean = false, skipIframeHide: boolean = false) {
    if (this.platform.is('cordova'))
      this.platform.ready().then(() => this.iterable.disableAutoDisplay()).catch((e) => LoggerProvider.info(this, `Iterable auto display pause failed: ${e}`));
    LoggerProvider.info(this, 'Logout user');
    this.lastSigninPassword = null;

    let welcomeBridgeEnabled: boolean = this.vueProvider && this.vueProvider.isAnyWelcomeArmEnabled();
    if (!welcomeBridgeEnabled && APP_CONFIG.env !== Env.PROD) {
      welcomeBridgeEnabled = APP_CONFIG.automationTest && this.vueProvider && this.vueProvider.isAnyWelcomeArmEnabled();
    }
    const useVueLogout = welcomeBridgeEnabled && !skipIframeHide && this.vueProvider && this.vueProvider.isConnected;

    try {
      await this.storageProvider.remove(StorageType.WelcomePendingCountryFrom);
      await this.storageProvider.remove(StorageType.WelcomePendingCountryTo);
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Failed to clear welcome pending countries on logout', ex);
    }

    let lastLogin: string;
    if (useVueLogout) {
      try {
        lastLogin = await this.storageProvider.get(StorageType.LastLogin);
      } catch (ex) {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Failed to get lastLogin for Vue logout', ex);
      }
    }

    if (showLoader)
      this.loadingProvider.show();
    if (HttpProvider.hasAuthCustomerId())
      this.httpProvider.resetAuthToken();
    await this.logout(true, true);

    if (useVueLogout) {
      try {
        await this.vueProvider.handleLogoutNavigation(lastLogin);
      } catch (ex) {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Failed Vue logout navigation, falling back', ex);
        try {
          await this.vueProvider.hideVueBridgeIframe();
        } catch (hideEx) {
          LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Failed to hide Vue container on logout fallback', hideEx);
        }
      }
      try {
        await this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage, { skipLogout: true, isAutoLogBackInBiometric: isAutoLogBackInBiometric });
      } catch (ex) {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Failed to reset Ionic nav stack during Vue logout', ex);
      }
    } else {
      if (!skipIframeHide) {
        try {
          if (this.vueProvider) {
            this.vueProvider.disconnectVueBridge();
          } else {
            const container = document.getElementById('vue-container');
            if (container)
              VueBridgeContainerHelpers.setContainerHidden(container as HTMLElement);
          }
        } catch (ex) {
          LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Failed to hide Vue container on logout', ex);
        }
      }
      if (page) {
        try {
          await this.routeProvider.setRoot(page, { skipLogout: true, isAutoLogBackInBiometric: isAutoLogBackInBiometric }); // we just logged out so don't need to logout again
        } catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to logout');
        }
      }
    }
    if (showLoader)
      this.loadingProvider.hide();
    this.homescreenProvider.setCachedMenuTitle(null);
  }

  resetTimer() {
    this.initTimer(true);
    }

  async initTimer(reset = false) {
    let log = true;
    if (reset)
      log = false;

    if (log)
      LoggerProvider.info(this, 'Init idle timer');
    this.unsubscribeTimer(log);
    if (this.logoutWarning) {
      const warning = await this.logoutWarning;
      if (warning) warning.dismiss();
      this.logoutWarning = null;
      }

    this.idleFunction = () => {
      this.resetTimer();
    };
    for (const ev of this.idleEvents) {
      document.body.addEventListener(ev, this.idleFunction);
      }

    this.idleTimeoutWarning = window.setTimeout(async() => {
      if (this.logoutWarning || HttpProvider.relogging) {
        LoggerProvider.info(this, 'Idle started but already handled');
        return;
      }
      LoggerProvider.info(this, 'Idle started - grace period for timeout (' + APP_CONFIG.timeoutTimerWarning + '/' + APP_CONFIG.timeoutTimer + ' seconds)');

      // check if there is a pending resume and wait first for it to be resolved
      HttpProvider.setLastActivity();
      if (HttpProvider.inactivityPromise) {
        LoggerProvider.info(this, 'Wait for resume for idle timeout');
        await HttpProvider.inactivityPromise;
      }

      if (AuthProvider.blockIdleTimeoutPromise) {
        LoggerProvider.info(this, 'Waiting for block idle timeout');
        await AuthProvider.blockIdleTimeoutPromise;
        LoggerProvider.info(this, 'Block idle timeout resolved');
      }

      if (this.skipRelogin('logout warning')) {
        this.unsubscribeTimer();
        this.logoutUser(this.routeProvider.pages.PreLoginPage, false);
        return;
      }
      if (this.httpProvider.isAuthTokenExpired()) {
        LoggerProvider.info(this, 'Idle started - already expired');
        this.processIdleTimeout();
        return;
      }
      this.showLogoutWarning();
    }, APP_CONFIG.timeoutTimerWarning * 1000);

    this.idleTimeout = window.setTimeout(() => {
      LoggerProvider.info(this, 'Idle timeout');
      this.processIdleTimeout();

    }, APP_CONFIG.timeoutTimer * 1000);
  }

  showLogoutWarning() {
    if (HttpProvider.relogging) {
      LoggerProvider.info(this, 'Show logout warning ignored because relogin is in progress');
      return;
    }

    this.logoutWarning = this.routeProvider.openActionSheetNew(this.routeProvider.pages.LogoutWarning, null, (ev) => {
      LoggerProvider.info(this, 'Idle stopped');
      this.logoutWarning = null;
      if (ev && ev.loggedOut) {
        LoggerProvider.info(this, 'Timeout in login warning modal');
        this.processIdleTimeout();
      } else {
        this.initTimer();
      }
    }, 'rmt-actionsheet-info-modal');
  }

  isLoggedIn() {
    return AuthProvider.isLoggedIn();
  }

  static isLoggedIn() {
    return HttpProvider.hasAuthCustomerId();
  }

  public async authorizationCodeExchange(payload: any, provider: string): Promise<string> {

    const mfaDeviceToken = await this.loginProvider.getMfaDeviceToken();
    payload['deviceId'] = LoggerProvider.uuid; // is like this.device.uuid but also supports a virtual uuid for browsers
    payload['DeviceSimCard'] = mfaDeviceToken;
    try {
      const response = await this.httpProvider.post(
        `${APP_CONFIG.API_URL}api/oauth/${provider}/token`,
        payload
      );
      if (response && response.Result && response.Result.RetVal == 100 && response.Value && response.Value.ExternalAuthSecurityCode) {
        return response.Value.ExternalAuthSecurityCode;
      } else {
        let message = response && response.Result ? response.Result.RetMsg : 'Unable to Authorization Code';
        LoggerProvider.info(this, 'Error during Authorization Code Exchange', message);
        throw new Error(message);
      }
    } catch (error) {
      // Optionally log the error here
      LoggerProvider.info(this, 'Error during Authorization Code Exchange', error);
      throw error.error || error;
    }
  }

  public async checkExternalUserExists(payload: any, provider: string): Promise<any> {

    const mfaDeviceToken = await this.loginProvider.getMfaDeviceToken();
    payload['deviceId'] = LoggerProvider.uuid; // is like this.device.uuid but also supports a virtual uuid for browsers
    payload['DeviceSimCard'] = mfaDeviceToken;

    try {
      const response = await this.httpProvider.post(
        `${APP_CONFIG.API_URL}api/oauth/${provider}/users/exists`,
        payload
      );
      return response;
    } catch (error) {
      // Optionally log the error here
      LoggerProvider.info(this, 'Error during Authorization Code Exchange', error);
      throw error.error || error;
    }
  }

  public async LinkExternalUserOnExists(payload: any, provider: string): Promise<any> {

    const mfaDeviceToken = await this.loginProvider.getMfaDeviceToken();
    payload['deviceId'] = LoggerProvider.uuid; // is like this.device.uuid but also supports a virtual uuid for browsers
    payload['DeviceSimCard'] = mfaDeviceToken;

    try {
      const response = await this.httpProvider.post(
        `${APP_CONFIG.API_URL}api/oauth/${provider}/users/link`,
        payload
      );
      return response;
    } catch (error) {
      // Optionally log the error here
      LoggerProvider.info(this, 'Error during Authorization Code Exchange', error);
      throw error.error || error;
    }
  }

  public async signin(payload: any, analyticsMethod, captchaSession, analyticsLocation="Log In", oauthProvider: string | null = null, isBridgeRequest = false): Promise<ISignin> {
    this.startLoginProfiler();

    const mfaDeviceToken = await this.loginProvider.getMfaDeviceToken();
    payload['deviceId'] = LoggerProvider.uuid; // is like this.device.uuid but also supports a virtual uuid for browsers
    payload['DeviceSimCard'] = mfaDeviceToken;

    const deviceTrustKey = this.getDeviceTrustStorageKey(payload.username);
    if (deviceTrustKey && !oauthProvider && !payload.fingerPrint) {
      const deviceTrustToken = await this.storageProvider.get(deviceTrustKey);
      if (deviceTrustToken)
        payload['DeviceTrustToken'] = deviceTrustToken;
    }

    let url = '';
    if (oauthProvider) {
      url = `${APP_CONFIG.API_URL}api/oauth/${oauthProvider}/signin`;
    } else {
      if (payload.fingerPrint)
        url = `${APP_CONFIG.API_URL}api/auth/fingerprint/V3/signin`; // new v3 signin call with user data
      else
        url = `${APP_CONFIG.API_URL}api/auth/V3/signin`;

      if (APP_CONFIG.featureFlags.signinOld) {
        if (payload.fingerPrint)
          url = `${APP_CONFIG.API_URL}api/auth/fingerprint/signin`;
        else
          url = `${APP_CONFIG.API_URL}api/auth/signin`;
      }
    }

    this.httpProvider.clearRefreshTimer();
    const payloadData = { ...payload };
    const username = payloadData.username;
    const lastLogin = await this.storageProvider.get(StorageType.LastLogin);
    this.requirePhoneUpdate = false;
    this.lastLogin = lastLogin;
    this.sessionLastLogin = lastLogin;

    this.lastSigninUsername = username;
    this.lastSigninPassword = null;
    if (username && this.sessionLastLogin && this.sessionLastLogin !== username) {
      await this.resetAnalytics();
      this.logRocket.resetLogRocketSession();
      this.chat.resetWidgetOpenedOnLoginChange()
    }
    this.loginChanged = lastLogin !== username;
    this.analyticsMethod = analyticsMethod;
    this.analyticsLocation = analyticsLocation;
    const handleSigninResponse = async (response: any) => {
      let signin = null;

      if (response.Value && response.Value.access_token && (response.Result.RetVal != LOGIN_ERRORS_NEW_TOKEN.MFA_MIGRATION_NOT_VERIFIED && response.Result.RetVal != LOGIN_ERRORS_NEW_TOKEN.VERIFY_PHONE_NUMBER && response.Result.RetVal != LOGIN_ERRORS_NEW_TOKEN.INVALID_GRANT)) {
        // login successful
        this.loginProvider.setDataToken(response);
        signin = AuthMapper.fromSigninEntity(response.Value);
        this.isFraudBlocked = response.Value && response.Value.IsFraudBlocked;

        this.requirePhoneUpdate = (signin && signin.authenticationResult) ? signin.authenticationResult.requirePhoneUpdate : false;

        if(payload.fingerPrint)
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.BiometricsVerificationSuccesful, analyticsData: {
              location: SegmentLocationNames.Login,
              method: analyticsMethod
            }
          });
          this.analytics.trackEvent({eventName: AnalyticsEventType.BiometricsVerificationCompleted, analyticsData: { // adding this event for failed and success. in future verification successful and failed events will be replaced by this.
              biometricsMethod: analyticsMethod, biometricsStatus: "enabled", country: APP_CONFIG.region.defaultCountryFrom,}});
      }

      if (!signin){
        if (response.Result && response.Result.RetVal === LOGIN_ERRORS_NEW_TOKEN.OTP_CHANNEL_PICKER) {
          const picker = response.Value && response.Value.OtpChannelPicker;
          if (!(picker && picker.PendingSigninToken))
            LoggerProvider.warning(this, LoggerEvent.CommunicationReceive, 'OTP channel picker (270) without PendingSigninToken, falling through');
        }
        // login not successful
        if (payload.fingerPrint) {
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.BiometricsVerificatioFailed, analyticsData: {
              location: SegmentLocationNames.Login,
              method: analyticsMethod
            }
          });
          // OM-19410: BE encrypts username into the fingerprint token, so an email change or account merge invalidates it. Drop the stale token to break the loop on next launch.
          const retVal = response && response.Result ? Number(response.Result.RetVal) : null;
          if (retVal === LOGIN_ERRORS_NEW_TOKEN.INVALID_GRANT) {
            LoggerProvider.info(this, 'Clearing stale biometric token after RetVal=INVALID_GRANT on fingerprint signin');
            this.removeBiometrics();
            await this.storageProvider.set(StorageType.EnableFirstFingerPrint, 'true');
          }
        }
        this.sendLoginFailedEventReason(analyticsLocation, analyticsMethod,
          (response && response.Result && response.Result.RetMsg) ? response.Result.RetMsg : "" )
        this.analytics.trackEvent({eventName: AnalyticsEventType.BiometricsVerificationCompleted, analyticsData: { // adding this event for failed and success. in future verification successful and failed events will be replaced by this.
          biometricsMethod: analyticsMethod, biometricsStatus: "failed", country: APP_CONFIG.region.defaultCountryFrom, failureReason: (response && response.Result) ? response.Result.RetMsg : ''}});
        this.sendLoginFailedEventReason(analyticsLocation, analyticsMethod, response);
        throw response;
      }
      if (username) {
        this.setLastLogin(username);
      }
      this.httpProvider.setAuthToken(signin.authToken);

      // Persist server-issued device token on every successful signin
      // (password / PIN / fingerprint). Without this HttpProvider.deviceToken
      // stays null/stale, getSession() ships an empty deviceToken to the Vue
      // iframe, and "Enable FaceID" in RMT4 settings fails with
      // "Device token is missing". Mirrors what the OTP dismissal path
      // already does in signin.ts:359-362.
      if (response.Value && response.Value.VerifiedDevice) {
        HttpProvider.deviceToken = response.Value.VerifiedDevice;
        this.storageProvider.set(StorageType.DeviceToken, response.Value.VerifiedDevice);
      }

      const firstTime = await this.storageProvider.get(StorageType.EnableFirstFingerPrint);
      if (firstTime) {
        this.loginChanged = true;
        this.storageProvider.remove(StorageType.EnableFirstFingerPrint);
      }
      this.setNewBiometricForExistingUser() // Set a new biometric for existing users if not set already, only for feature-enabled country
      this.login = response.Value;
      return signin;
    };

    const forcedLoginError = this.debugError.getForcedError(DebugErrorFlow.Login);
    if (forcedLoginError != null) {
      return handleSigninResponse({ Result: { RetVal: forcedLoginError }, Value: {} });
    }

    return this.httpProvider.post(url, payloadData, handleSigninResponse, { captchaSession }, isBridgeRequest);
  }

  async setLastLogin(username) {
    try {
      AuthProvider.lastLogin = username;
      if (username !== await this.storageProvider.get(StorageType.LastLogin)) {
        this.removeBiometrics();
        this.orderProvider.resetCachePreConfirmOrder();
      }
      this.storageProvider.set(StorageType.LastLogin, username);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Unable to set last login');
    }
  }

  // Unlike setLastLogin, keeps biometrics when the same customer switches between email and phone.
  async setLastLoginFromBridge(identifier: string) {
    try {
      if (identifier !== await this.storageProvider.get(StorageType.LastLogin)) {
        const enrolledFor = await this.storageProvider.get(StorageType.LastBiometricCustomerId);
        if (!enrolledFor || enrolledFor !== await this.getCurrentCustomerIdForBiometrics()) {
          await this.removeBiometrics();
        }
        this.orderProvider.resetCachePreConfirmOrder();
        // The stored phone country belongs to the previous identifier.
        await this.storageProvider.set(StorageType.LastLoginCountry, '');
      }
      AuthProvider.lastLogin = identifier;
      this.lastSigninUsername = identifier;
      await this.storageProvider.set(StorageType.LastLogin, identifier);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Unable to set last login from bridge');
    }
  }

  // Same id enableBiometrics stores in LastBiometricCustomerId.
  private async getCurrentCustomerIdForBiometrics(): Promise<string | null> {
    if (this.loginProvider.isLiteUser()) {
      const userId = this.httpProvider.getAuthUserId();
      return userId ? userId.toString() : null;
    }
    const customer = await this.customerProvider.getCustomer();
    return customer && customer.id ? customer.id.toString() : null;
  }

  async biometricsAvailable() {
    const result = await this.nativeProvider.biometrics.isAvailable();
    // TODO !!!!!!!!!!!!!!!!!!! get rid of them ?
    this.faceId = this.nativeProvider.biometrics.faceId;
    this.touchId = this.nativeProvider.biometrics.touchId;
    return result;
  }

  getBiometricsType(): BiometricsType {
    return this.faceId ? BiometricsType.FaceId : BiometricsType.TouchId;
  }

  getBiometricsMethod(method: string): SignInMethod {
    let signinMethod;
    if(method === 'faceID') signinMethod = SignInMethod.FaceId;
    else if (method === 'touchID') signinMethod = SignInMethod.TouchId;
    else signinMethod = SignInMethod.Unknown;

    return signinMethod;
  }

  getBiometricsTypeContent(translate): string {

    const faceIdText = this.platform.is('ios') ? 'global.myRia.jsonContent.Settings.FaceID' : 'global.myRia.jsonContent.Settings.FacialRecognition';
    const touchIdText = this.platform.is('ios') ? 'global.myRia.jsonContent.Settings.TouchID' : 'global.myRia.jsonContent.Settings.FingerprintRecognition';

    if (this.faceId) return translate.instant(faceIdText);
    else return translate.instant(touchIdText);
  }

  getBiometricIcon(): string {
    if (this.platform.is('ios')) {
      return this.faceId
        ? 'assets/icons/faceid.svg'
        : 'assets/icons/touchid.svg';
    }

    return 'assets/icons/fingerprint-recognition.svg';
  }

  async scanBiometrics() {
    return this.nativeProvider.biometrics.scan();
  }

  // Pass showFailedModal=false where the native modal can't be seen; an unseen modal never resolves.
  async getStoredFingerprintToken(method: string, showFailedModal = true): Promise<string | null> {
    return (await this.readStoredFingerprintToken(method, showFailedModal)).token;
  }

  async readStoredFingerprintToken(method: string, showFailedModal = true): Promise<IStoredFingerprintTokenResult> {
    const store = await this.scanBiometrics();

    if (store === BIOMETRIC_KEY_INVALIDATED) {
      await this.failStoredBiometrics(method, LoginFailureReason.BIOMETRIC_INVALIDATED, showFailedModal);
      return { token: null, reason: 'invalidated' };
    }
    
    const storedDataObj = this.parseKeychainPayload(store);
    
    if (storedDataObj && storedDataObj.fingerPrintToken) {
      return { token: storedDataObj.fingerPrintToken };
    }
    // OM-19410 + OM-19076: a non-empty keychain STRING that yields no usable token is corrupt/stale —
    // wipe to break the stale-token loop and surface the modal/analytics. scan() resolves false on
    // cancel/fail, which must NOT clear enrollment.
    if (typeof store === 'string' && store) {
      await this.failStoredBiometrics(method, LoginFailureReason.MALFORMED_STORED_TOKEN, showFailedModal);
      return { token: null, reason: 'malformed' };
    }
    // iOS returns no payload on a Face ID cancel, so an empty result counts as cancelled.
    const iosNoPayload = this.platform.is('ios') && (store === null || store === undefined);
    const cancelled = this.nativeProvider.biometrics.lastScanError === 'Cancelled' || iosNoPayload;
    return { token: null, reason: cancelled ? 'cancelled' : 'missing' };
  }

  private async failStoredBiometrics(method: string, reason: LoginFailureReason, showFailedModal = true): Promise<null> {
    this.sendLoginFailedEventReason(SegmentLocationNames.Login, method, null, reason);
    await this.removeBiometrics();
    if (showFailedModal) {
      await this.showBiometricLoginFailedModal();
    }
    return null;
  }

  private async showBiometricLoginFailedModal() {
    try {
      await this.routeProvider.waitForTransitions();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed waiting for transitions before the biometric login failed modal');
    }
    await new Promise<void>((resolve) => {
      const opened = this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
        img: 'assets/imgs/warning-circle-background.svg',
        title: this.translate.instant('page.errorPage.loginFailed'),
        subtitle: this.translate.instant('login.error.biometric_auth_failed.message'),
        primaryButtonText: this.translate.instant('page.login.biometric.signInAnotherWay')
      }, () => { resolve(); });
      if (!opened)
        resolve();
    });
  }

  /*
  don't use as it will prompt the biometrics dialog !
  isBiometricEnabled(): Promise<any> {
    return this.fingerprint.isAvailable().then (
      ()=> this.fingerprint.has(ConfigProvider.settings.keychainKey)
    )
  }
  */

  saveBiometrics(fingerPrintToken, userAuthenticationRequired = true): Promise<any> {
    return this.nativeProvider.biometrics.save(fingerPrintToken, userAuthenticationRequired);
  }

  private parseKeychainPayload(store: string): IBiometricsKeychainPayload | null {
    if (!store) {
      return null;
    }
    try {
      return JSON.parse(store);
    } catch (parseEx) {
      LoggerProvider.exception(this, parseEx, LoggerEvent.ProviderException, 'Failed to parse biometrics Keychain payload');
      return null;
    }
  }

  removeBiometrics(): Promise<any> {
    return this.nativeProvider.biometrics.remove();
  }

  async clearBiometricsAfterPasswordChange(): Promise<void> {
    try {
      const deviceToken = await this.storageProvider.get(StorageType.FingerprintDeviceToken);
      if (!(deviceToken && deviceToken.length > 0))
        return;
      LoggerProvider.info(this, 'Clearing biometric token after password change');
      await this.storageProvider.set(StorageType.EnableFirstFingerPrint, 'true');
      await this.removeBiometrics();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to clear biometric token after password change');
    }
  }

  registerFingerprint(form: IRegisterFingerprintEntity): Promise<IRegisterFingerprint> {
    let url = `${APP_CONFIG.API_URL}api/auth/V3/fingerprint`;
    if (APP_CONFIG.featureFlags.signinOld)
      url = `${APP_CONFIG.API_URL}api/auth/fingerprint`;
    return this.httpProvider.post(url, form, (response) => {
      return {
        fingerPrint : response.Value.FingerPrint,
        result : response.Result
      }
    });
  }

  registerBiometricToken(form: IRegisterBiometricFingerprintEntity): Promise<IRegisterFingerprint> {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/Auth/RegisterSecurityToken`, form, (response) => {
      return {
        fingerPrint : response.Value.Token,
        result : response.Result
      }
    });
  }

  enableBiometrics(translate, password: string = null, canShowFeedBack: boolean = true): Promise<boolean> {
    return new Promise(async (resolve) => {
      try {
        await this.enableBiometricsAsync(translate, resolve, password, canShowFeedBack);
      } catch (ex) {
        RmtSnackbarComponent.present(this.translate.instant('page.authorizeBiometricsPage.biometricsDeniedAccess'));
        resolve(false);
      }
    });
  }

  private async enableBiometricsAsync(translate, resolve, password: string, canShowFeedBack: boolean = true) {
    const enableNewBiometrics: boolean = await this.isNewBiometricsRegisterEnable();
    // Awaited: an unawaited throw here leaves `resolve` uncalled and the caller pending.
    if (password || enableNewBiometrics) await this.processBiometrics(resolve, password, canShowFeedBack);
    else {
      this.routeProvider.openActionSheetNew(this.routeProvider.pages.BiometricEnablePage, {onResolve: resolve, faceId: this.faceId});

      // const alert = this.alertCtrl.create({
      //   title: this.getBiometricsTypeContent(translate),
      //   message: `${this.contentProvider.instant('page.authorizeBiometricsPage.passwordBiometricsEnabledContent')} ${this.getBiometricsTypeContent(translate)}`,
      //   inputs: [
      //     {
      //       name: 'password',
      //       placeholder: this.contentProvider.instant('page.authorizeBiometricsPage.password.label'),
      //       type: 'password',
      //     },
      //   ],
      //   buttons: [
      //     {
      //       text: this.contentProvider.instant('global.genericCancelButton'),
      //       role: 'cancel',
      //       handler: () => {
      //         resolve(false);
      //       },
      //     },
      //     {
      //       text: this.contentProvider.instant('page.authorizeBiometricsPage.registerText'),
      //       handler: (data) => {
      //         this.processBiometrics(resolve, data.password);
      //       },
      //     },
      //   ],
      // });
      // alert.setMode('ios');
      // alert.present();
    }
  }

  async processBiometrics(resolve, password, canShowFeedBack: boolean = true) {
    let result = {
      enabled: false,
      error: null
    };
    this.loadingProvider.show();
    try {
      let username = await this.loginProvider.getUserName();
      const isLiteUser = this.loginProvider.isLiteUser();
      let customer;
      if(!isLiteUser) {
        customer = await this.customerProvider.getCustomer();
        // username = customer.emailAddress;
      }
      // else
      //   username = this.httpProvider.getAuthEmail();
      if (!username)
        username = await this.storageProvider.get(StorageType.LastLogin);
      let deviceToken = HttpProvider.deviceToken
      if (!deviceToken)
        deviceToken = await this.storageProvider.get(StorageType.DeviceToken);
      const mfaDeviceToken = await this.loginProvider.getMfaDeviceToken();
      const params = {
        username: username,
        password: password,
        deviceToken: deviceToken,
        deviceId: LoggerProvider.uuid,
        DeviceSimCard: mfaDeviceToken,
      };
      const paramsBiometric = {
        LoginType: LoginPasswordType.BIO,
        DeviceToken: deviceToken,
        DeviceId: LoggerProvider.uuid,
        DeviceSimCard: mfaDeviceToken,
      };
      const enableNewBiometrics: boolean = await this.isNewBiometricsRegisterEnable();
      const token = enableNewBiometrics ? await this.registerBiometricToken(paramsBiometric) : await this.registerFingerprint(params);
      if (token && token.fingerPrint) {
        await this.removeBiometrics();
        await this.saveBiometrics(token.fingerPrint);
        // OM-19665 (iOS): KeychainTouchId.save() does NOT reject when the user cancels the Face/Touch ID
        // prompt, so confirm enablement with an explicit verify before persisting it. scan() resolves false
        // on cancel. Android's save() already rejects on cancel (handled by the catch below), so gate iOS
        // only — otherwise Android would prompt twice.
        const verified = !this.platform.is('ios') || await this.scanBiometrics();
        if (!verified) {
          await this.removeBiometrics();
          result.enabled = false;
        } else {
          if (deviceToken) this.storageProvider.set(StorageType.FingerprintDeviceToken, deviceToken);
          else this.storageProvider.set(StorageType.FingerprintDeviceToken, mfaDeviceToken);
          this.storageProvider.set(StorageType.LastBiometricCustomerId, isLiteUser ? this.httpProvider.getAuthUserId().toString() : customer.id.toString());
          const biometricsTypeContent = this.getBiometricsTypeContent(this.translate);
          if(canShowFeedBack === true){
            RmtSnackbarComponent.present(this.translate.instant('page.authorizeBiometricsPage.enabledText', {'biometricsType' : biometricsTypeContent}));
          }

          result.enabled = true;
        }
      } else {
        RmtSnackbarComponent.present(this.translate.instant('page.authorizeBiometricsPage.biometricsDeniedAccess'));
        this.removeBiometrics();
        result.enabled = false;
        if(token && token.result) {
          result.error = token.result.RetMsg;
        }
      }
    } catch (ex) {
      // Must never throw: the resolve() below settles the caller's promise.
      try {
        RmtSnackbarComponent.present(this.translate.instant('page.authorizeBiometricsPage.biometricsDeniedAccess'));
        await this.removeBiometrics();
      } catch (cleanupEx) {
        LoggerProvider.exception(this, cleanupEx, LoggerEvent.ProviderException, 'Biometrics cleanup failed after enable error');
      }
      result.enabled = false;
    }
    this.loadingProvider.hide();

    resolve(result);
  }

  async mustAcceptLTLicense() {
    return new Promise(async (resolve) => {
      let locale = RmtTranslateComponent.getRedirectLanguage();
      if (this.login && (this.login.MustAcceptedNewLicense || this.login.RequireTermsAndCondition)) {
        let params = {
          showRightClose: true,
          showBack: false,
          closeLicensePage: true,
          img: 'assets/imgs/cancelOrder.svg',
          title: this.translate.instant('global.termsAgreementContainer.termsAgreementHeader'),
          msg: this.translate.instant('global.termsAgreementContainer.termsAgreementContent').replace('{{locale}}', locale),
          primaryButtonText: this.translate.instant('global.termsAgreementContainer.agreeButton'),
          primaryButtonAction: async () => {
            try {
              this.loadingProvider.show();
              if (this.login.MustAcceptedNewLicense)
                await this.acceptLTLicense();
              else if (this.login.RequireTermsAndCondition)
                await this.acceptRiaOrWmtLicense()
              this.loadingProvider.hide();
              resolve(true);
            } catch (ex) {
              RmtPopupComponent.showErrorWithLogTranslation("page.errorPage.licenceAcceptanceFailed");
              this.loadingProvider.hide();
            }
          }
        };
        this.loadingProvider.hide();
        this.routeProvider.replace(this.routeProvider.pages.InfoPage, params);
      }
      else resolve(true);
    });
  }

  async acceptLTLicense(): Promise<any> {
    const result = await this.loginProvider.updateCustomerSetting(
      CUSTOMER_SETTINGS.LT_LICENSE_TERMS_AND_CONDITIONS.AREA,
      CUSTOMER_SETTINGS.LT_LICENSE_TERMS_AND_CONDITIONS.TYPE, 1,
      this.httpProvider.getAuthCustomerId()).toPromise();

    if (this.login) {
      this.login.MustAcceptedNewLicense = false;
      this.login.RequireTermsAndCondition = false;
    }
    return result;
  }

  async acceptRiaOrWmtLicense(): Promise<any> {
    if (APP_CONFIG.branding.branding == Branding.Walmart)
      return this.loginProvider.updateCustomerSetting(
        CUSTOMER_SETTINGS.WMT_TERMS_CONDITIONS.AREA,
        CUSTOMER_SETTINGS.WMT_TERMS_CONDITIONS.TYPE, 1,
        this.httpProvider.getAuthCustomerId()).toPromise();
    else
      return this.loginProvider.updateCustomerSetting(
        CUSTOMER_SETTINGS.RIA_TERMS_CONDITIONS.AREA,
        CUSTOMER_SETTINGS.RIA_TERMS_CONDITIONS.TYPE, 1,
        this.httpProvider.getAuthCustomerId()).toPromise();
  }

  async getUserCountries() {
    const countries = [];
    const country: any[] = await this.resourceProvider.getCountries();
    let registrationEnabledCountries = country.filter(country => country.isRegistrationEnabled == true);
    const useRegistrationCountryFlag = registrationEnabledCountries && registrationEnabledCountries.length > 0;

    if(!useRegistrationCountryFlag) registrationEnabledCountries = APP_CONFIG.regions;

    for (let i = 0; i < registrationEnabledCountries.length; i++) {
      let region = registrationEnabledCountries[i];
      if (useRegistrationCountryFlag) {
        region = APP_CONFIG.regions.find(r => r.defaultCountryFrom === registrationEnabledCountries[i].value);
        if (!region) continue;
      }
      if (!APP_CONFIG.automationTest && APP_CONFIG.region.isEu && (!region.isEu || region.originalCountryAllowedOnly)) // for EU only allow other selectable EU countries to be selected
        continue;
      countries.push(region.defaultCountryFrom);
    }
    return countries;
  }

  getUserCountry() {
    return this.httpProvider.getAuthCountry();
  }

  async setUserCountry(country, save: boolean = false, reset: boolean = true, fieldReset: boolean = true, reuseLanguage: boolean = true, waitForFields: boolean = true, setLanguage: boolean = true) {
    if ((HttpProvider.authCountry !== country || this.resourceProvider.userCountryCode !== country) && !fieldReset) {
      LoggerProvider.info(this, `Change user country from ${HttpProvider.authCountry} to ${country} - force field reset`);
      fieldReset = true;
    }

    this.invalidCountry = false;
    const result = await this.httpProvider.updateAuthCountry(country);
    if (save)
      {
        this.storageProvider.set(StorageType.UserCountry, HttpProvider.authCountry);
        this.resourceProvider.userCountryCode = HttpProvider.authCountry;
      }

    if (!result) {
      this.invalidCountry = true;
      return;
    }

    if (reset) this.httpProvider.resetAuthToken();
    if (!fieldReset && AuthProvider.userCountryFieldsVersion && AuthProvider.userCountryFieldsVersion !== this.httpProvider.getHeaderVersion()) {
      fieldReset = true;
      LoggerProvider.info(this, `Forced reset user country fields because version has changed from ${AuthProvider.userCountryFieldsVersion} to ${this.httpProvider.getHeaderVersion()}`);
    }
    if (fieldReset)
      await this.resetUserCountryFields(waitForFields);
  }

  async resetUserCountryFields(waitForFields, registration = true) {
    AuthProvider.userCountryFieldsVersion = this.httpProvider.getHeaderVersion();
    this.resourceProvider.initializeResources(true, true);
    this.formProvider.reset(); // so they get reloaded when needed
    if (registration) {
      if (waitForFields) await this.formProvider.getFieldsRegistration(true);
      else this.formProvider.getFieldsRegistration(true);
    }
  }

  async getAppLocale() {
    let locale = null;
    if(this.platform.is('cordova')) {
      const preferredLang = await this.globalization.getPreferredLanguage();
      if(!(preferredLang && preferredLang.value)) locale = await this.globalization.getLocaleName();
      else locale = preferredLang;
    }

    return locale;
  }

  getLanguageFromCountryLangs(lang: string) {
    const langs = this.utilsProvider.availableLanguageCultures();
    const language = langs.find(language => { return language.indexOf(lang.substr(0, 2).toLowerCase()) === 0});
    return language;
  }

  async registerIterable() {
    LoggerProvider.info(this, 'Start Iterable registration');

    const customerId = this.resolveIterableId();
    LoggerProvider.info(this, 'Iterable set id ' + customerId);

    // SN always uses the placeholder identifier so push device registration and in-app REST polling target the same Iterable record. If we switched to a real email mid-lifecycle the SDK would create a second user and orphan the device on the placeholder record
    const email = this.getIterableIdentityEmail();
    LoggerProvider.info(this, 'Iterable set email ' + email);
    if (this.platform.is('cordova') && email)
      Iterable.setEmail(email);

    if (this.platform.is('cordova')) {
      const isPushEnabled = await this.notificationProvider.isPushEnabled(customerId);
      if (isPushEnabled == 'true' || isPushEnabled === null)
        Iterable.registerForPush((response) => {
          LoggerProvider.info(this, 'Registered successfully in Iterable', response);
        });
      else
        Iterable.disablePush((response) => {
          LoggerProvider.info(this, 'Disabled push notification in Iterable', response);
        });
    }
  }

  async saveUserInfo(setMarketing: boolean = false, skipMarketing: boolean = false, registrationMarketingOptIn: boolean = null) {
    const country = this.httpProvider.getAuthIsoCode();
    if (this.loginProvider.isLiteUser()) {
      if (country)
        await this.setUserCountry(country, true, false, false);
      await this.setCustomerLanguage();
      this.loginProvider.identifyLiteCustomer(null, setMarketing, skipMarketing, registrationMarketingOptIn);
      this.resourceProvider.initializeResources(true,true);
    }
    else {
      if (country)
        await this.setUserCountry(country, true, false, false, true, true, false);
      await this.setCustomerLanguage();
    }
  }

  async saveUserCredentialsInKeychain(username: string, password: string) {
    if (APP_CONFIG.featureFlags.usAuthPluginPasswordHint) {
      if (this.platform.is('cordova')) {
        const origin = (this.platform.is('ios')) ? 'riamoneytransfer.com' : 'https://www.riamoneytransfer.com';

        try {
          const success = await AutofillPlugin.saveCredentials(username, password, origin)
          if (success) {
            LoggerProvider.info(this, 'AutofillPlugin credential saved successfully', success);
          } else {
            throw ("Credential save was unsuccessful")
          }
        } catch (ex) {
          LoggerProvider.error(this, LoggerEvent.PluginError, 'AutofillPlugin credential error', ex);
        }
      }
    } else 
      return null
  }

  getLiteUserCountry(user: User) {
    let userCountrySetting = user.Settings.find(setting => setting.SettingType === USER_FEATURES.PREFERRED_COUNTRY.TYPE);
    let region = APP_CONFIG.regions.find(region => region.regionId === parseInt(userCountrySetting.SettingValue));
    return region.defaultCountryFrom;
  }

  async setCustomerLanguage() {
    const language = await this.customerProvider.getCurrentLanguage();
    const storeLanguage = await this.storageProvider.get(StorageType.Language);

    const accountLanguage = this.httpProvider.getAuthCultureCode();
    let userLanguage = accountLanguage;

    // The account language wins over the phone one (e.g. a Welcome pick) and is never written back at login.
    // Only an account without a language takes the phone language.
    if (!accountLanguage && storeLanguage) {
      userLanguage = storeLanguage;

      try {
        this.loginProvider.updateSetting(USER_FEATURES.LANGUAGE.AREA, USER_FEATURES.LANGUAGE.TYPE, userLanguage).subscribe({
          complete: () => {
            LoggerProvider.info(this, 'Updated setting for language to BE');
          },
          error: (ex) => {
            LoggerProvider.error(this,ex,  LoggerEvent.ExceptionMessage, 'Failed to update setting for language to BE');
          }
        });
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ExceptionMessage, 'Failed to update setting for language to BE');
      }
    }

    if (userLanguage) {
      const langs = this.utilsProvider.availableLanguageCultures();
      // it seems that the backend saves the user language for en always as en-US hence we might get the wrong en value from the backend and hence we double check that the user language is really available and correct it otherwise
      if (userLanguage != language || langs.indexOf(userLanguage) === -1) {
        userLanguage = userLanguage.substr(0, 2);
        var languageCulture;
        if (APP_CONFIG.region.languageCultures)
          languageCulture = APP_CONFIG.region.languageCultures.find(e => e.includes(userLanguage))
        if (languageCulture && langs.includes(languageCulture))
          this.customerProvider.setLanguage(languageCulture, false, false);
        else if (APP_CONFIG.region.defaultLanguageCulture && APP_CONFIG.region.defaultLanguageCulture.includes(userLanguage))
          this.customerProvider.setLanguage(APP_CONFIG.region.defaultLanguageCulture, false, false);
        else {
          const defaultLanguage = langs.find(e => e.indexOf(userLanguage) === 0);

          if(!accountLanguage && this.loginProvider.isLiteUser() && storeLanguage != defaultLanguage) {
            this.customerProvider.setLanguage(storeLanguage, false, false); // TODO: Added this line because store language is not set. Due to the culture code not coming correctly.
          } else {
            this.customerProvider.setLanguage(defaultLanguage, false, false);
          }
        }
      }
    }
  }

  async signinBiometrics(fingerprintToken, deviceToken) {
    const method = this.faceId ? 'faceID' : 'touchID';
    this.loadingProvider.show();
    const biometricsMethod = this.getBiometricsMethod(method);
    const params = {
      biometricsMethod: biometricsMethod,
      fingerPrint: fingerprintToken,
      deviceToken: deviceToken,
      deviceId: LoggerProvider.uuid,
    };

    try {
      await this.signin(params, method, null); // for biometrics captcha token is not needed
      const result = await this.initializeUser();
      if (result) {
        await this.loginFirstRedirection();
        this.loadingProvider.hide();
        return true;
      }
      else  {
        this.loadingProvider.hide();
        RmtPopupComponent.showErrorWithLogTranslation("page.errorPage.automaticLoginFailed");
      }
    } catch (ex) {
      let title = null;
      let message = ex.errorMessage;
      let messageKey = "";
      if ((ex.errorCurrent || {}).issueId === ErrorCodeType.InvalidSecurityQuestionAnswerAttempts) {
        messageKey = 'page.signInFailedPage.ria_errormessage_customerdisabled_supportcodemessage';
      } else if (ex.errorCode === ErrorCodeType.CustomerLoginAttemptsExceeded || ex.errorCode === ErrorCodeType.CustomerDisabled) {
        messageKey = 'page.signInFailedPage.ria_errormessage_customerdisabled_resetpassword';
        const lastlogin = await this.storageProvider.get(StorageType.LastLogin);
        this.routeProvider.push(this.routeProvider.pages.SigninFailedPage, { error: ex.errorCode, username: lastlogin });
      }

      if (!message) {
        title = 'page.registrationFinishPage.logInFailedMessage';
      }

      if(messageKey) {
        RmtPopupComponent.showExceptionWithLogTranslation(this, ex, messageKey, title);
      } else {
        const titleMsg = title ? this.translate.instant(title) : null;
        RmtPopupComponent.showException(this, ex, message, titleMsg);
      }

    }
    this.loadingProvider.hide();

    return false;
  }

  async proceedOtpLogin(data, result) {
    this.login = result;
    if (data && data.username)
      this.setLastLogin(data.username)
    this.loginProvider.setDataToken(result);
    const signin = AuthMapper.fromSigninEntity(result);
    this.httpProvider.setAuthToken(signin.authToken);

    await this.storeDeviceTrustToken(data, result);
  }

  public async storeDeviceTrustToken(data, result) {
    const deviceTrustToken = result && result.DeviceTrustToken;
    const deviceTrustKey = this.getDeviceTrustStorageKey(data && data.username);
    if (deviceTrustToken && deviceTrustKey)
      await this.storageProvider.set(deviceTrustKey, deviceTrustToken);
  }

  private getDeviceTrustStorageKey(username): string {
    const normalized = String(username || '').trim().toLowerCase();
    if (!normalized)
      return null;
    return StorageType.DeviceTrustToken + '-' + md5(normalized);
  }

  async handleAuthErrors(page,ex,method,data,proceedLoginCallback) {
    this.loadingProvider.hide();
    let otpResponse = null;
    if(ex.headers) {
      if(this.platform.is("cordova"))
        otpResponse = Object.keys(ex.headers);
      else
          otpResponse = ex.headers;
    }
    if(ex.status === 307){
      return await this.platform.ready().then(async() =>{
        const otpTimerResponse = this.otpTimerProvider.getCooldownPropsFromHeader(ex.headers);
        let otpObject = {otpTimerResponse: otpTimerResponse};

        otpResponse.forEach((value) => {
          if(value == 'waitingotptime')
            otpObject[value] = Date.parse(this.httpProvider.getHeadersValue(ex.headers, 'waitingotptime'));
          else
            otpObject[value] = this.httpProvider.getHeadersValue(ex.headers,value);
        });
        const otpObjects = AuthMapper.fromOtpHeader(otpObject);
        this.loadingProvider.hide();
        const result = await this.utilsProvider.openModalOTP(otpObjects, data);
        if(result) {
          if(result['blocked']){
            const [email, phoneNo]  = this.utilsProvider.getEmailAndPhoneNo(data.username)
            this.analytics.trackEvent({eventName: AnalyticsEventType.PhoneVerificationFailed, analyticsData: {
              phoneCountry: data.selectedCountry,
              phoneNumber: phoneNo,
              email:email,
              location: PHONE_VERIFICATION_EVENT_LOCATION.LOGIN,
              phoneVerificationMethod: (result['analyticsVoice']) ? SegmentLocationNames.VoicePhoneVerfication : SegmentLocationNames.SmsPhoneVerification,
              reason: 'Max attempts exceeded'
            }});
            const redirection = () => { this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage, true)};
            this.routeProvider.push(this.routeProvider.pages.RegistrationStepVerifyFailed, {onBack: redirection, onClose: redirection});
            this.loadingProvider.hide();
          }else{
            await this.proceedOtpLogin(data, result);
            await proceedLoginCallback(result);
          }
        } else
          this.loadingProvider.hide();
        return result;
      });
    }
    else {
      let title = null;
      let message = ex.errorMessage;
      let error = null;

      if ((ex.errorCurrent || {}).issueId === ErrorCodeType.InvalidSecurityQuestionAnswerAttempts) {
        message = this.translate.instant('page.signInFailedPage.ria_errormessage_customerdisabled_supportcodemessage');
      }
      else if (ex.errorCode === ErrorCodeType.CustomerLoginAttemptsExceeded ||
        ex.errorCode === ErrorCodeType.CustomerDisabled) {
        message = this.translate.instant('page.signInFailedPage.ria_errormessage_customerdisabled_supportcodemessage');
        const lastlogin = await this.storageProvider.get(StorageType.LastLogin);
        this.routeProvider.push(this.routeProvider.pages.SigninFailedPage, { error: ex.errorCode, username: lastlogin });
      }else if(ex.errorCode === OTP_RESULT.SEND_MFA_MAX_ATTEMPTS.GENERAL){
        this.routeProvider.push(this.routeProvider.pages.RegistrationStepVerifyFailed);
      }

      if (ex.Result)
        error = ex.Result.RetVal;

      const httpStatus = ex && (ex.status !== undefined ? ex.status : (ex.errorCurrent && ex.errorCurrent.status));
      if (httpStatus === 401) {
        const isBiometricFlow = method === 'faceID' || method === 'touchID';
        this.sendLoginFailedEventReason(this.analyticsLocation, this.analyticsMethod, ex);
        if (isBiometricFlow) {
          await this.showBiometricLoginFailedModal();
        } else {
          this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
            img: 'assets/imgs/warning-circle-background.svg',
            title: this.translate.instant('page.errorPage.loginFailed'),
            subtitle: this.translate.instant('login.error.invalid_credentials.message'),
            primaryButtonText: this.translate.instant('page.signInPage.forgotPasswordLink'),
            primaryAction: () => {
              this.routeProvider.push(this.routeProvider.pages.SignInForgotResetPassPage, {
                loginName: data && data.username,
                loginCountry: data && data.selectedCountry
              });
            },
            showSecondaryButton: true,
            secondaryButtonText: this.translate.instant('global.genericCancelButton'),
          });
        }
        this.loadingProvider.hide();
        return;
      }


      if(error === LOGIN_ERRORS_NEW_TOKEN.BLOCKED_BY_OTP_FAILED_ATTEMPS || error === OTP_RESULT.SEND_MFA_MAX_ATTEMPTS.SIGNIN){
        LoggerProvider.warning(this, LoggerEvent.CustomerLocked, 'Customer has been locked by OTP failed attemps');
        const redirection = () => { this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage)};
        this.routeProvider.push(this.routeProvider.pages.RegistrationStepVerifyFailed, {onBack: redirection, onClose: redirection});
      }
      else if (error === LOGIN_ERRORS_NEW_TOKEN.BLOCKED_USER) {
        LoggerProvider.warning(this, LoggerEvent.CustomerLocked, 'Customer has been locked');
        this.showAccountBlockedPage();
      }

      else if(error === LOGIN_ERRORS_NEW_TOKEN.OTP_LOOKUP_FAILED
        || error === LOGIN_ERRORS_NEW_TOKEN.MFA_PREFERENCE_NOT_SUPPORTED
        || error === LOGIN_ERRORS_NEW_TOKEN.PHONE_NUMBER_TYPE_NOT_SUPPORTED) {
        LoggerProvider.warning(this, LoggerEvent.PageException, 'OTP could not be sent during login');
        this.routeProvider.push(this.routeProvider.pages.SigninFailedPage, { error: ErrorCodeType.OtpUnavailable });
      }
      else if(error === LOGIN_ERRORS_NEW_TOKEN.OTP_ERRORS) {
        LoggerProvider.warning(this, LoggerEvent.PageException, 'OTP error during login');
        this.routeProvider.push(this.routeProvider.pages.SigninFailedPage);
      }
      else if(error === LOGIN_ERRORS_NEW_TOKEN.NO_CONTACT_DATA) {
        LoggerProvider.warning(this, LoggerEvent.PageException, 'Cutomer has no contact data');
        this.routeProvider.push(this.routeProvider.pages.SigninFailedPage);
      }
      else if ( error === LOGIN_ERRORS_NEW_TOKEN.BLOCKED_PASSWORD_USER || error === LOGIN_ERRORS_NEW_TOKEN.BLOCKED_PASSWORD_QUESTIONS) {
        LoggerProvider.warning(this, LoggerEvent.CustomerLocked, 'Invalid signin BLOCKED_PASSWORD_USER or BLOCKED_PASSWORD_QUESTIONS');
        if(APP_CONFIG.region.region === Region.SN){
          this.walletProvider.showWalletTempBlockedAndPinDisabledPage(data.username);
        } else{
          this.routeProvider.push(this.routeProvider.pages.SigninFailedPage, { error: ErrorCodeType.CustomerLoginAttemptsExceeded, username: data.username });
        }
      }
      else if (error == LOGIN_ERRORS_NEW_TOKEN.VERIFY_PHONE_NUMBER || error == LOGIN_ERRORS_NEW_TOKEN.MFA_MIGRATION_NOT_VERIFIED) {
        if (error == LOGIN_ERRORS_NEW_TOKEN.MFA_MIGRATION_NOT_VERIFIED)
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.MFAMigrationStarted,
            analyticsData: {
              location: 'login'
            }
          });
          let phone = {
            "countryCode": ex.Value.CountryCode,
            "phoneNumber": this.phoneProvider.stripDuplicateCountryCode(ex.Value.CountryCode, ex.Value.Phone)
          }
        this.routeProvider.push(this.routeProvider.pages.MobileVerifyPage, { signin: data, phone: phone, migration: true });
      }
      else if (error == LOGIN_ERRORS_NEW_TOKEN.DUPLICATE_ACCOUNT) {
        let targetPage = this.routeProvider.pages.DuplicationAccountErrorPage;
        let targetParams = {
          primaryButtonAction: () => {this.routeProvider.pop();},
          trackEvent: AnalyticsEventType.ContactModalDisplayed,
          trackParams: {
            location: SegmentLocationNames.Login,
            reason: ContactModalReasons.RegistrationDuplicatedUser
          },
          secondaryButtonAction: () => { this.call.callCustomerService(SegmentLocationNames.Login, ContactModalReasons.RegistrationDuplicatedUser); },
        };
        this.routeProvider.push(targetPage, targetParams);
      }
      else if (error == LOGIN_ERRORS_NEW_TOKEN.BLOCKED_CANNOT_SEND) {
        LoggerProvider.warning(this, LoggerEvent.PageException, 'Customer Account is not active');
        let params = {
          showClose: true,
          showHelp: true,
          img: 'assets/imgs/Warning.svg',
          title: this.translate.instant('Page.accountNotActive.title'),
          msg: this.translate.instant('Page.accountNotActive.description'),
          primaryButtonText: this.translate.instant('page.sendFailedPage.contactUsButton'),
          trackEvent: AnalyticsEventType.ContactModalDisplayed,
          trackParams: {
            location: SegmentLocationNames.Login,
            reason: ContactModalReasons.CustomerAccountNotActive
          },
          primaryButtonAction: () => {
            this.contactSupport(ContactModalReasons.CustomerAccountNotActive);
          }
        };
        this.routeProvider.replace(this.routeProvider.pages.InfoPage, params);
      }
      else if (error === LOGIN_ERRORS_NEW_TOKEN.INVALID_PASS && this.loginProvider.isPINEnabledCountry && this.loginProvider.wrongPasscodeEmitter){
        this.loginProvider.wrongPasscodeEmitter.emit(LOGIN_ERRORS_NEW_TOKEN.INVALID_PASS);
      }
      else if (this.otpTimerProvider.isTwilioUnavailable(ex)) {
        this.otpTimerProvider.handleTwilioUnavailable();
        this.loadingProvider.hide();
      }
      else if (this.otpTimerProvider.isOtpSendBlocked(ex)) {
        this.otpTimerProvider.handleOtpSendBlocked();
      }
      else if (error === LOGIN_ERRORS_NEW_TOKEN.INVALID_GRANT && data && data.fingerPrint) {
        this.loadingProvider.hide();
        await this.showBiometricLoginFailedModal();
      }
      else if (!this.httpProvider.hasConnectionProblem()) {
        this.loadingProvider.hide();
        let title = null;
        let message = null;
        let reason = "Credentials";
        if (ex.errorCode === ErrorCodeType.CustomerDisabled)
          message = ex.errorMessage;

        const messageHtml = document.createElement('div');
        messageHtml.innerHTML = message;
        if (!message){
          title = 'page.registrationFinishPage.logInFailedMessage';
          RmtPopupComponent.showExceptionWithLogTranslation(this, ex, message, title, messageHtml);
        }else
        {
          RmtPopupComponent.showException(this, ex, message, title, messageHtml);
        }
        RmtTranslateComponent.parseLinks([ messageHtml ], this.routeProvider, false, data.username);
      }
    }
  }

  contactSupport(reason = '') {
    if (APP_CONFIG.region.defaultCountryFrom == 'AU') {
      const emailTo = 'au_support@riamoneytransfer.com';
      let options = {
        to: emailTo,
      };
      this.email.open(options);
    } else {
      this.call.callCustomerService(SegmentLocationNames.Login, reason);
    }
  }

  sendLoginFailedEventReason(location, loginMethod, response, failureReasonCode?: LoginFailureReason) {
    if (location && loginMethod) {
      let failureReason = failureReasonCode || this.getResponseRetMsg(response);
      if (this.faceId && typeof failureReason === 'string' && failureReason.toLowerCase() === 'invalid fingerprint') {
        failureReason = 'Invalid Face ID';
      }
      if(APP_CONFIG.region.region === Region.SN){
        // location is the flow (always Log In here), not the login entry point, so it joins Passcode Challenge Successful
        this.analytics.trackEvent({ eventName: AnalyticsEventType.PasscodeChallengeFailed, analyticsData: {
          country: APP_CONFIG.region.defaultCountryFrom, location: PasscodeChallengeLocation.LogIn, failureReason: failureReason,
          errorCode: UtilsProvider.getResultRetVal(response)
         } });
      }
      this.analytics.trackEvent({
        eventName: AnalyticsEventType.LoginFailed, analyticsData: {
          location: location,
          loginMethod: loginMethod,
          failureReason: this.normalizeFailureReason(failureReason),
          phoneCountryCode: APP_CONFIG.region.defaultCountryCodeFrom
        }
      });
    }
  }

  // Split PascalCase/camelCase backend codes ('UserNotFound') into readable values ('User Not Found') for analytics.
  private normalizeFailureReason(failureReason: any): any {
    if (typeof failureReason !== 'string') {
      return failureReason;
    }
    return failureReason.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  private getResponseRetMsg(response: any): string {
    if (!response) {
      return "";
    }
    if (response.error && response.error.Result && response.error.Result.RetMsg) {
      return response.error.Result.RetMsg;
    }
    if (response.Result && response.Result.RetMsg) {
      return response.Result.RetMsg;
    }
    return "";
  }

  async resetAnalytics() {
    LoggerProvider.info(this, 'Reset Analytics - Username changed');
    try {
      const setting = await this.getSegmentResetSetting();
      if (setting == COUNTRY_SETTINGS_ENABLE_VALUE)
        await this.analytics.resetForUserChange();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to reset analytics on username change');
    }
    this.sessionLastLogin = null;
  }

  private getSegmentResetSetting(): Promise<any> {
    let timer;
    const timeout = new Promise((resolve) => {
      timer = window.setTimeout(() => {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Timed out reading segment reset setting, skipping analytics reset');
        resolve(null);
      }, SEGMENT_RESET_SETTING_TIMEOUT_MS);
    });
    const lookup = this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.DIGITAL_SEGMENT_RESET_AFTER_LOGOUT)
      .then((setting) => {
        window.clearTimeout(timer);
        return setting;
      }, (ex) => {
        window.clearTimeout(timer);
        throw ex;
      });
    return Promise.race([lookup, timeout]);
  }

  getUserFirstName() {
    if (this.login && this.login.FirstName)
      return this.login.FirstName;
    return '';
  }

  getUserFullName() {
    if (this.login && this.login.FirstName && this.login.LastName) {
      return this.login.FirstName + ' ' + this.login.LastName;
    } else if (this.login && this.login.FirstName) {
      return this.login.FirstName;
    }
  }

  showAccountBlockedPage(){
    this.routeProvider.openModal(this.routeProvider.pages.AccountBlockedPage, {
      primaryButtonAction: () => {
        this.routeProvider.setRoot(this.routeProvider.pages.SigninPage);

        const link = this.translate.instant('global.linkAccountClosedHelpArticle');
        this.routeProvider.openIab(link);
      }, onClose: () => {
        this.routeProvider.setRoot(this.routeProvider.pages.SigninPage);
      }
    })
  }

  async processIdleTimeout() {
    if (HttpProvider.relogging) {
      LoggerProvider.info(this, 'Idle timeout ignored because relogin is in progress');
      return;
    }

    const result = await this.checkRelogin();
    if (!result) {
      if (this.platform.is('cordova')) this.iterable.disableAutoDisplay();
      if(this.fintocProvider.widget) this.fintocProvider.widget.destroy();
      await this.logoutUser();
    }
  }

  skipRelogin(type) {
    const page = this.routeProvider.getLastActivePage();
    const pageRoot = this.routeProvider.getLastRootPage();
    if (page === this.routeProvider.pages.PreLoginPage.page || page === this.routeProvider.pages.SigninPage.page ||
        pageRoot === this.routeProvider.pages.PreLoginPage.page || pageRoot === this.routeProvider.pages.SigninPage.page) {
      LoggerProvider.info(this, 'Ignored ' + type + ' since user is not logged in');
      return true;
    }
    return false;
  }

  async checkRelogin() {
    LoggerProvider.info(this, 'Start relogin');
    if (!AuthProvider.allowRelogin) {
      LoggerProvider.info(this, 'Relogin not allowed');
      return false;
    }

    let result: any = false;
    if (!HttpProvider.readyPromise) {
      LoggerProvider.info(this, 'Blocking communications during relogin');
      HttpProvider.readyPromise = new Promise((resolve) => { HttpProvider.readyResolve = resolve; });
    }
    try {
      LoggerProvider.info(this, 'Check relogin for page ' + this.routeProvider.getLastActivePage() + ' / ' + this.routeProvider.getLastRootPage());
      const time = (new Date().getTime() - this.httpProvider.lastRefresh.getTime()) / 1000;
      if (time >= APP_CONFIG.timeoutRelogin) {
        LoggerProvider.info(this, 'Logged out due to inactivity ('+ time + ' > ' + APP_CONFIG.timeoutRelogin + ' seconds)')
        RmtPopupComponent.showToast(this.translate.instant('page.authorizeBiometricsPage.automaticLogOutMessage'));
        HttpProvider.readyResolve(false);
        return false;
      }
      if (this.skipRelogin('relogin')) {
        LoggerProvider.info(this, 'Ignored relogin since user is not logged in');
        HttpProvider.readyResolve(false);
        return false;
      }

      HttpProvider.relogging = true;
      this.unsubscribeTimer();
      if (this.logoutWarning) {
        LoggerProvider.info(this, 'Relogin waiting for login warning');
        const warning = await this.logoutWarning;
        console.log(warning);
        if (warning) warning.dismiss();
        this.logoutWarning = null;
      }
      LoggerProvider.info(this, 'Trying to relogin with biometrics');
      this.httpProvider.clearRefreshTimer();
      this.storageProvider.remove(StorageType.RefreshToken);

      result = await this.biometricsLogin();
      if (!result && AuthProvider.isLoggedIn()) {
        const useVueRelogin = this.vueProvider.canShowVueRelogin();
        const lastLogin = useVueRelogin ? await this.storageProvider.get(StorageType.LastLogin) : null;
        result = await new Promise((resolve) => {
          const logoutTime = (APP_CONFIG.timeoutRelogin - time);
          const logoutTimer = window.setTimeout(() => {
            LoggerProvider.info(this, 'Aborted resignin due to inactivity (' + logoutTime + ' > ' + APP_CONFIG.timeoutRelogin + ' seconds)');
            RmtPopupComponent.showToast(this.translate.instant('page.authorizeBiometricsPage.automaticLogOutMessage'));
            this.vueProvider.cancelVueRelogin(false);
            resolve(false);
          }, logoutTime * 1000);
          const finish = (reloginResult) => {
            if (logoutTimer)
              window.clearTimeout(logoutTimer);
            LoggerProvider.info(this, 'Received relogin result: ' + reloginResult);
            resolve(reloginResult);
          };
          const showNativeRelogin = () => {
            // the native SigninPage is passcode-only on wallet builds and cannot complete a relogin
            if (this.walletProvider.isWalletSupportEnabled()) {
              LoggerProvider.warning(this, LoggerEvent.Vue, 'RMT5 relogin unavailable on a wallet build, logging out');
              finish(false);
              return;
            }
            LoggerProvider.info(this, 'Show relogin');
            setTimeout(() => {
              this.routeProvider.openModal(this.routeProvider.pages.SigninPage, { redirected: false, isFromReLogin: true }, finish);
            }, 500);
          };
          if (useVueRelogin) {
            LoggerProvider.info(this, 'Show relogin in RMT5');
            this.vueProvider.showVueRelogin(lastLogin).then((vueResult) => {
              if (vueResult === 'native')
                showNativeRelogin();
              else
                finish(vueResult);
            });
          } else {
            showNativeRelogin();
          }
        });
      }
      if (result) {
        LoggerProvider.info(this, 'Relogin successful');
        this.initTimer();
        this.httpProvider.startRefreshTimer();
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Exception during relogin');
      result = false;
    }
    LoggerProvider.info(this, 'Finished relogin with result ' + result);
    HttpProvider.relogging = false;
    HttpProvider.readyResolve(result);
    HttpProvider.readyPromise = null;
    return result;
  }

  async biometricsLogin() {
    let result = false;
    let hideLoader = false;

    try {
      const deviceToken = await this.storageProvider.get(StorageType.FingerprintDeviceToken); // controls isBiometricEnabled
      const available = await this.biometricsAvailable();
      if (deviceToken && available) {
        const method = (this.faceId) ? 'faceID' : 'touchID';
        const fingerprintToken = await this.getStoredFingerprintToken(method);
        if (fingerprintToken) {
          this.loadingProvider.show();
          hideLoader = true;
          const biometricsMethod = this.getBiometricsMethod(method);
          const tokenReference = await this.storageProvider.getSetUserTokenReference(false)
          const params = {
            biometricsMethod: biometricsMethod,
            fingerPrint: fingerprintToken,
            deviceToken: deviceToken,
            deviceId: LoggerProvider.uuid,
            LoginType: LoginPasswordType.BIO,
            TokenReference: tokenReference
          }
          const signin = await this.signin(params, method, null); // for biometrics captcha token is not needed
          if (signin)
            result = true;
        }
      }
    } catch (ex) {
      RmtPopupComponent.showException(this, ex, 'Failed to relogin with biometrics');
    }

    if (hideLoader)
      this.loadingProvider.hide();
    return result;
  }

  async logLoginSuccessfulEvent() {
    const walletAndCardStatusData = await this.walletProvider.getWalletAndCardStatusSegEv();
    if(this.analyticsMethod) {
      this.analytics.queueIdentifyEvent({ eventName: AnalyticsEventType.LoginSuccessful, analyticsData: { 
        loginMethod: this.analyticsMethod,
        location: this.analyticsLocation,
        walletStatus: walletAndCardStatusData.walletStatus,
        physicalCardStatus: walletAndCardStatusData.physicalCardStatus || null,
        virtualCardStatus: walletAndCardStatusData.virtualCardStatus || null, 
        phoneCountryCode:  APP_CONFIG.region.defaultCountryCodeFrom
      }});
    }
    this.analyticsMethod = "";
    this.analyticsLocation = "";
  }

  async isNewBiometricsRegisterEnable(){
    const enableNewBiometricsSetting = await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.ENABLE_NEW_BIOMETRICS_REGISTER);
    return this.utilsProvider.normalizeToBoolean(enableNewBiometricsSetting)
  }

  async isNewBiometricsLoginEnableUser(){
    const enableNewBiometricsLogin = await this.loginProvider.getUserSettingAsync(CUSTOMER_SETTINGS.CUSTOMER_SETUP_NEW_BIOMETRIC.TYPE);
    return this.utilsProvider.normalizeToBoolean(enableNewBiometricsLogin)
  }

  async setNewBiometricForExistingUser(){
    try{
      const enableNewBiometricsSetting = await this.isNewBiometricsRegisterEnable();
      if(enableNewBiometricsSetting){
        LoggerProvider.info(this, 'New biometric registration feature is enabled for country');
        const [deviceToken, mfaDeviceToken, customer, isNewBiometricsLoginEnableUser] = await Promise.all([
          this.storageProvider.get(StorageType.FingerprintDeviceToken),
          this.loginProvider.getMfaDeviceToken(),
          this.customerProvider.getCustomer(),
          this.isNewBiometricsLoginEnableUser()
        ]);
        if(deviceToken && deviceToken.length > 0 && !isNewBiometricsLoginEnableUser){
          const isLiteUser = this.loginProvider.isLiteUser();
          LoggerProvider.info(this, 'User already has biometric registered and change with new as feature is enable for country');
          const paramsBiometric = {
            LoginType: LoginPasswordType.BIO,
            DeviceToken: deviceToken,
            DeviceId: LoggerProvider.uuid,
            DeviceSimCard: mfaDeviceToken,
          }
          const token = await this.registerBiometricToken(paramsBiometric);
          LoggerProvider.info(this, 'Register biometric token response');
          if (token && token.fingerPrint) {
            await this.saveBiometrics(token.fingerPrint);
            if (deviceToken) this.storageProvider.set(StorageType.FingerprintDeviceToken, deviceToken);
            else this.storageProvider.set(StorageType.FingerprintDeviceToken, mfaDeviceToken);
            this.storageProvider.set(StorageType.LastBiometricCustomerId, isLiteUser ? this.httpProvider.getAuthUserId().toString() : customer.id.toString());
          }
        }
      }
    } catch(ex){
      LoggerProvider.error(this, LoggerEvent.CaptchaException, 'Failed to register new biometric for existing user', ex);
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/authorization/auth.ts